import React, { useEffect, useRef, useState } from 'react'
import { toast, ToastContainer } from 'react-toastify';
import axios from 'axios';
import { AUTHH, BASE_URL } from '../../../config/axios';
import { formatDate, formattedDateLong } from '../../Date';
import { Projects } from '../../FilterDropdown';
import { CheckGreen, CircularGrid, CrossRed, Filter, Office, WarnYellow, Attachment, Eye, DocumentIcon } from '../../AllSvg';
import { DateRangePicker, formatCurrencyIndian, formatDateTime, getMonthName, handleErrorToast, inputMaxLimit, inputMinLimit, ProgressModal, ViewDocumnetsbilling } from '../../CustomFunctions';
import { ApprovedVehicleBillingAdminC1, RejectVehicleBillingAdminC1 } from '../../VehicleBillingdetails/VehicleBillingActionButton';
import { DownloadImprestBill } from '../../SiteManagement/BulkModals';
import { Modal } from 'react-bootstrap';
import { Check, CheckCheck, CheckCircle, CheckIcon, Cross, CrossIcon, Edit3, FileText, IndianRupee, Loader, LucideCross, LucideFileWarning, Save, Wallet, X, XCircle } from 'lucide-react';


const DynamicRequestLogTimeline = ({ i, detailedCheckKeys }) => {
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    // Function to format timestamp
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    const formatValue = (value) => {
        if (value === null || value === undefined) return 'null';
        if (typeof value === 'object') {
            // Handle nested objects in dynamic_approval
            if (value.id && (value.role || value.employee)) {
                return value.role
                    ? `Role: ${value.role.role_name} (${value.role.id})`
                    : `Employee: ${value.employee.name} (${value.employee.id})`;
            }
            return JSON.stringify(value, null, 2);
        }
        // Handle long strings (not just URLs)
        if (typeof value === 'string' && value.length > 50) {
            return `${value.substring(0, 25)}...${value.substring(value.length - 25)}`;
        }
        return value.toString();
    };

    const renderObjectValue = (value) => {
        if (value === null) return 'null';
        if (typeof value !== 'object') return value;

        return (
            <div className="nested-object">
                {Object.entries(value).map(([k, v]) => (
                    <div key={k} className="nested-pair">
                        <span className="nested-key">{k}:</span>
                        <span className="nested-value">
                            {typeof v === 'object' ? renderObjectValue(v) : v}
                        </span>
                    </div>
                ))}
            </div>
        );
    };


    const getChangedFields = (prevObj, currentObj) => {
        if (!prevObj || !currentObj) return [];

        const changedFields = [];
        const allKeys = new Set([...Object.keys(prevObj), ...Object.keys(currentObj)]);

        allKeys.forEach(key => {
            // Skip these fields as they're not relevant changes
            if (['created_at', 'updated_at', 'id', 'dynamic_approval', 'other_details'].includes(key)) return;

            const prevValue = prevObj[key];
            const currentValue = currentObj[key];

            // Handle nested objects (like role, employee)
            if (typeof prevValue === 'object' && prevValue !== null && !Array.isArray(prevValue)) {
                const nestedChanges = getConditionalDetailChangedFields(prevValue, currentValue || {});
                if (nestedChanges.length > 0) {
                    changedFields.push({
                        field: key,
                        from: JSON.stringify(prevValue),
                        to: JSON.stringify(currentValue)
                    });
                }
                return;
            }

            // Compare arrays (like other_details)
            if (Array.isArray(prevValue)) {
                // Simple comparison for arrays - could be enhanced
                if (JSON.stringify(prevValue) !== JSON.stringify(currentValue)) {
                    changedFields.push({
                        field: key,
                        from: 'Previous array values',
                        to: 'Updated array values'
                    });
                }
                return;
            }

            // Compare primitive values
            if (prevValue !== currentValue) {
                changedFields.push({
                    field: key,
                    from: prevValue !== null && prevValue !== undefined ? prevValue.toString() : 'null',
                    to: currentValue !== null && currentValue !== undefined ? currentValue.toString() : 'null'
                });
            }
        });

        return changedFields;
    };

    const getDetailedChangedFields = (prevObj, currentObj, parentKey = '') => {
        if (!prevObj || !currentObj) return [];

        const changedFields = [];
        const allKeys = new Set([...Object.keys(prevObj), ...Object.keys(currentObj)]);

        allKeys.forEach(key => {
            const prevValue = prevObj[key];
            const currentValue = currentObj[key];
            const fullKey = parentKey ? `${parentKey}.${key}` : key;

            // Handle nested objects
            if (typeof prevValue === 'object' && prevValue !== null && !Array.isArray(prevValue)) {
                const nestedChanges = getConditionalDetailChangedFields(prevValue, currentValue || {}, fullKey);
                changedFields.push(...nestedChanges);
                return;
            }

            // Handle arrays (deep comparison)
            if (Array.isArray(prevValue)) {
                if (!Array.isArray(currentValue) || prevValue.length !== currentValue.length ||
                    JSON.stringify(prevValue) !== JSON.stringify(currentValue)) {
                    changedFields.push({
                        field: fullKey,
                        from: JSON.stringify(prevValue),
                        to: JSON.stringify(currentValue)
                    });
                }
                return;
            }

            // Handle primitive values
            if (prevValue !== currentValue) {
                changedFields.push({
                    field: fullKey,
                    from: prevValue !== null && prevValue !== undefined ? prevValue.toString() : 'null',
                    to: currentValue !== null && currentValue !== undefined ? currentValue.toString() : 'null'
                });
            }
        });

        return changedFields;
    };

    const getConditionalDetailChangedFields = (prevObj, currentObj, parentKey = '', detailedCheckKeys = []) => {
        if (!prevObj || !currentObj) return [];

        const changedFields = [];
        const allKeys = new Set([...Object.keys(prevObj), ...Object.keys(currentObj)]);

        allKeys.forEach(key => {
            const prevValue = prevObj[key];
            const currentValue = currentObj[key];
            const fullKey = parentKey ? `${parentKey}.${key}` : key;

            // Skip always-ignored system fields
            if (['created_at', 'updated_at', 'id'].includes(key)) return;

            // Check if the field should be deeply inspected
            const shouldDeepCheck = detailedCheckKeys.includes(key) || detailedCheckKeys.includes(parentKey);

            // Handle nested objects or arrays
            if (typeof prevValue === 'object' && prevValue !== null && currentValue !== null) {
                if (shouldDeepCheck) {
                    const nestedChanges = getConditionalDetailChangedFields(prevValue, currentValue, fullKey, detailedCheckKeys);
                    changedFields.push(...nestedChanges);
                } else {
                    if (JSON.stringify(prevValue) !== JSON.stringify(currentValue)) {
                        changedFields.push({
                            field: fullKey,
                            from: JSON.stringify(prevValue),
                            to: JSON.stringify(currentValue)
                        });
                    }
                }
                return;
            }

            // Primitive values comparison
            // if (prevValue !== currentValue) {
            //     changedFields.push({
            //         field: fullKey,
            //         from: prevValue !== null && prevValue !== undefined ? prevValue.toString() : 'null',
            //         to: currentValue !== null && currentValue !== undefined ? currentValue.toString() : 'null'
            //     });
            // }

            if (prevValue !== currentValue) {
                changedFields.push({
                    field: parentKey ? `${parentKey}.${key}` : key,
                    from: formatValue(prevValue),
                    to: formatValue(currentValue)
                });
            }
        });

        return changedFields;
    };


    return (
        <>
            <button title="View Logs Timeline" onClick={handleShow} className="log-timeline-button">
                <Eye />
            </button>

            <Modal show={show} onHide={handleClose} dialogClassName="log-timeline-modal" size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Change Log Timeline</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="timeline-container">
                        {i?.change_log?.length > 1 ? (
                            <div className="timeline">
                                {i.change_log.map((logEntry, index) => {
                                    const prevLog = index > 0 ? i.change_log[index - 1].log : null;
                                    const detailedCheckKeys = ['other_details', 'dynamic_approval'];
                                    const changes = getConditionalDetailChangedFields(prevLog, logEntry.log, '', detailedCheckKeys);

                                    if (changes.length === 0) return null;

                                    return (
                                        <div key={index} className={`timeline-item ${index === 0 ? 'first' : ''} ${index === i.change_log.length - 1 ? 'last' : ''}`}>
                                            <div className="timeline-marker"></div>
                                            <div className="timeline-content">
                                                <div className="timeline-header">
                                                    <span className="timeline-date">{formatDate(logEntry.changed_at)}</span>
                                                    <span className="badge">{changes.length} changes</span>
                                                </div>

                                                <div className="changes-list">
                                                    {changes.map((change, changeIndex) => (
                                                        <div key={changeIndex} className="change-item">
                                                            <div className="change-field">{change.field}:</div>

                                                            <div className="change-details">
                                                                <span className="from-value">{change.from}</span>
                                                                <span className="arrow">→</span>
                                                                {/* <span className="to-value">{change.to}</span> */}
                                                                <span className="to-value">
                                                                    {typeof change.to === 'object' ? renderObjectValue(change.to) : change.to}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="no-logs">No change logs available</div>
                        )}
                    </div>

                </Modal.Body>
            </Modal>
        </>
    );
};


const DynamicRequestImprestApproval = ({ i, getDynamicRequestData }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => {
        setFormData({
            approved_project_imprest_monthly: i.imprest_details?.approved_project_imprest_monthly ? i.imprest_details?.approved_project_imprest_monthly : i.imprest_details?.fixed_imprest_amount,
            approved_stationary_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_stationary_item : i.imprest_details?.stationary_item,
            approved_electronic_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_electronic_item : i.imprest_details?.electronic_item,
            approved_grocery: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_grocery : i.imprest_details?.grocery,
            other_details: i.imprest_details?.other_details?.map((other) => ({
                other_name: other.other_name,
                amount: other.amount,
                approved_other: i.imprest_details?.change_log?.length > 1 ? other.approved_other : other.amount,
            })),
            dynamic_approval: i?.imprest_details?.dynamic_approval
        })
        setShow(true)
    };

    const [formData, setFormData] = useState({
        approved_project_imprest_monthly: i.imprest_details?.approved_project_imprest_monthly ? i.imprest_details?.approved_project_imprest_monthly : i.imprest_details?.fixed_imprest_amount,

        approved_stationary_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_stationary_item : i.imprest_details?.stationary_item,
        approved_electronic_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_electronic_item : i.imprest_details?.electronic_item,
        approved_grocery: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_grocery : i.imprest_details?.grocery,
        other_details: i.imprest_details?.other_details?.map((other) => ({
            other_name: other.other_name,
            amount: other.amount,
            approved_other: i.imprest_details?.change_log?.length > 1 ? other.approved_other : other.amount,
        })),
        dynamic_approval: i?.imprest_details?.dynamic_approval
    });

    const totalApproved =
        Number(formData.approved_stationary_item) +
        Number(formData.approved_electronic_item) +
        Number(formData.approved_grocery) +
        Number(formData.approved_project_imprest_monthly) +
        (formData.other_details?.length > 0
            ? formData.other_details?.reduce((total, other) => total + Number(other.approved_other || 0), 0)
            : 0);

    const approverLabel = i.employee ? (i.role ? `${i.employee?.name} (${i.role?.role_name})` : i.employee?.name) : i.role?.role_name;

    const handleFormSubmitPut = async (e) => {
        e.preventDefault();
        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(0);

        const loadingToastId = toast.loading("Loading: Please wait...");

        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            if (progress >= 100) {
                progress = 100;
                clearInterval(interval);
            }
            setUploadProgress(progress);
        }, 100);

        let ImprestFormData = {
            approved_stationary_item: formData.approved_stationary_item,
            approved_electronic_item: formData.approved_electronic_item,
            approved_grocery: formData.approved_grocery,
            other_details: formData.other_details,
            approved_project_imprest_monthly: formData.approved_project_imprest_monthly,
        };

        try {
            let imp = await axios.put(`${BASE_URL}/siteproject/imprest/${i.imprest_details?.id}/`,
                ImprestFormData,
            );

            if (imp.status === 200 || imp.status === 201) {
                let req = await axios.put(`${BASE_URL}/siteproject/dynamicapprovalspermissionsimprest/${i.id}/`, { ...i, approval_status: "approved" });

                if (req.status === 200 || req.status === 201) {
                    clearInterval(interval);
                    toast.dismiss(loadingToastId);
                    toast.success("Imprest Request Approved Successfully");
                    await getDynamicRequestData();
                    handleClose();
                } else {
                    clearInterval(interval);
                    setUploadProgress(0);
                    toast.error(`Imprest Request Update Error: ${req.statusText}`);
                }
            } else {
                clearInterval(interval);
                setUploadProgress(0);
                toast.error(`Imprest Update Error: ${imp.statusText}`);
            }
        } catch (err) {
            clearInterval(interval);
            setUploadProgress(0);
            handleErrorToast(err, loadingToastId)
        } finally {
            clearInterval(interval);
            setTimeout(() => {
                setShowProgressModal(false);
            }, 1500);
            setLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    return (
        <>
            <style>{`
                .diap-info-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 12px 20px;
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 14px 18px;
                    margin-bottom: 18px;
                }
                .diap-info-item {
                    display: flex;
                    flex-direction: column;
                    gap: 3px;
                }
                .diap-info-label {
                    font-size: 11px;
                    font-weight: 600;
                    color: #94a3b8;
                    text-transform: uppercase;
                    letter-spacing: 0.4px;
                }
                .diap-info-value {
                    font-size: 13.5px;
                    color: #1f2937;
                    font-weight: 600;
                }
                .diap-amount-card {
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 14px 18px;
                }
                .diap-amount-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 7px 0;
                    font-size: 13.5px;
                    color: #475569;
                }
                .diap-amount-row:not(:last-child) {
                    border-bottom: 1px dashed #e2e8f0;
                }
                .diap-amount-row.total {
                    font-weight: 700;
                    color: #1f2937;
                    font-size: 15px;
                    padding-top: 10px;
                }
            `}</style>

            <button className="model-delete-button-modern actionn-icon" style={{ backgroundColor: "#00000075" }} title={`Imprest Stage ${i.level} Approval`} onClick={handleShow}>
                <CheckIcon size={20} color='white' />
            </button>

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Add" />
            ) : (
                <Modal show={show} onHide={handleClose} dialogClassName="modern-project-modal" size="lg">
                    <Modal.Header className="modern-modal-header">
                        <div className="header-content">
                            <div style={{ color: "white" }}>
                                <CheckCircle size={20} />
                            </div>
                            <div>
                                <Modal.Title>Confirm Stage {i.level} Approval</Modal.Title>
                                <p className="header-subtitle" style={{ color: "white" }}>
                                    {approverLabel} • Imprest "{i.imprest_details?.imprest_name}"
                                </p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={handleClose}>
                            <X size={20} />
                        </button>
                    </Modal.Header>

                    <Modal.Body className="modern-modal-body">
                        <div className="diap-info-grid">
                            <div className="diap-info-item">
                                <span className="diap-info-label">Project Code</span>
                                <span className="diap-info-value">{i.imprest_details?.project_code}</span>
                            </div>
                            <div className="diap-info-item">
                                <span className="diap-info-label">Project Name</span>
                                <span className="diap-info-value">{i.imprest_details?.project_short_name}</span>
                            </div>
                            <div className="diap-info-item">
                                <span className="diap-info-label">Date</span>
                                <span className="diap-info-value">{formattedDateLong(i.imprest_details?.date)}</span>
                            </div>
                            <div className="diap-info-item">
                                <span className="diap-info-label">Days</span>
                                <span className="diap-info-value">{i.imprest_details?.no_of_days}</span>
                            </div>
                        </div>

                        <div className="section-title">
                            <Wallet size={18} />
                            <h4>Approved Amount Breakdown</h4>
                        </div>

                        <div className="diap-amount-card">
                            {i.imprest_details?.fixed_imprest_amount > 0 && (
                                <div className="diap-amount-row">
                                    <span>Monthly Imprest</span>
                                    <span>{formatCurrencyIndian(formData.approved_project_imprest_monthly)}</span>
                                </div>
                            )}
                            {i.imprest_details?.stationary_item > 0 && (
                                <div className="diap-amount-row">
                                    <span>Stationary</span>
                                    <span>{formatCurrencyIndian(formData.approved_stationary_item)}</span>
                                </div>
                            )}
                            {i.imprest_details?.electronic_item > 0 && (
                                <div className="diap-amount-row">
                                    <span>Electronics</span>
                                    <span>{formatCurrencyIndian(formData.approved_electronic_item)}</span>
                                </div>
                            )}
                            {i.imprest_details?.grocery > 0 && (
                                <div className="diap-amount-row">
                                    <span>Grocery</span>
                                    <span>{formatCurrencyIndian(formData.approved_grocery)}</span>
                                </div>
                            )}
                            {i.imprest_details?.other_details?.length > 0 && i.imprest_details.other_details.some(other => other.amount > 0) && (
                                formData.other_details?.map((other, index) => (
                                    i.imprest_details?.other_details[index].amount > 0 && (
                                        <div className="diap-amount-row" key={index}>
                                            <span>{other.other_name ? other.other_name : `Other-${index + 1}`}</span>
                                            <span>{formatCurrencyIndian(other.approved_other)}</span>
                                        </div>
                                    )
                                ))
                            )}
                            <div className="diap-amount-row total">
                                <span>Total Approved Amount</span>
                                <span>{formatCurrencyIndian(totalApproved)}</span>
                            </div>
                        </div>

                        <div className="form-actions">
                            <button type="button" className="btn-secondaryy" onClick={handleClose}>
                                Cancel
                            </button>
                            <button type="submit" className="btn-primaryy" onClick={handleFormSubmitPut} disabled={loading}>
                                {loading ? <Loader size={18} className="spinning" /> : <Check size={18} />}
                                <span>{loading ? 'Confirming...' : 'Confirm'}</span>
                            </button>
                        </div>

                        <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};

const DynamicRequestImprestEditApproval = ({ i, getDynamicRequestData }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => {
        setFormData({
            approved_project_imprest_monthly: i.imprest_details?.approved_project_imprest_monthly ? i.imprest_details?.approved_project_imprest_monthly : i.imprest_details?.fixed_imprest_amount,

            approved_stationary_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_stationary_item : i.imprest_details?.stationary_item,
            approved_electronic_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_electronic_item : i.imprest_details?.electronic_item,
            approved_grocery: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_grocery : i.imprest_details?.grocery,
            other_details: i.imprest_details?.other_details?.map((other) => ({
                other_name: other.other_name,
                amount: other.amount,
                approved_other: i.imprest_details?.change_log?.length > 1 ? other.approved_other : other.amount,
            })),
            amount_change_reason: "",
        })
        setErrors({});
        setInputState({});
        setShow(true)
    };

    const [formData, setFormData] = useState({
        approved_project_imprest_monthly: i.imprest_details?.approved_project_imprest_monthly ? i.imprest_details?.approved_project_imprest_monthly : i.imprest_details?.fixed_imprest_amount,

        approved_stationary_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_stationary_item : i.imprest_details?.stationary_item,
        approved_electronic_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_electronic_item : i.imprest_details?.electronic_item,
        approved_grocery: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_grocery : i.imprest_details?.grocery,
        other_details: i.imprest_details?.other_details?.map((other) => ({
            other_name: other.other_name,
            amount: other.amount,
            approved_other: i.imprest_details?.change_log?.length > 1 ? other.approved_other : other.amount,
        })),
        amount_change_reason: "",
    });

    //******************Validation Start********************* */
    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ["amount_change_reason"];
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name.startsWith('approved_')) {

            // For individual expenses like transportation, accommodation, fooding, etc.
            if (!name.startsWith('approved_other_')) {
                const itemAmountKey = name.replace('approved_', '');
                const currentValue = i[itemAmountKey] !== undefined && i[itemAmountKey] !== null ? i[itemAmountKey] : 0;
                const maxLength = getNumberLength(currentValue);

                // Ensure the input value does not exceed the allowed length
                if (value.length <= maxLength) {
                    const numericValue = value.trim() ? parseInt(value, 10) : 0;

                    // Check if the numeric value is less than or equal to the current value
                    if (numericValue <= currentValue) {
                        setInputState({
                            ...inputState,
                            [name]: value.trim() ? "success" : "",
                        });

                        setFormData({
                            ...formData,
                            [name]: value,
                        });

                        // Clear any previous error
                        setErrors({
                            ...errors,
                            [name]: "",
                        });
                    } else {
                        // Handle case where the numeric value exceeds the current value
                        setErrors({
                            ...errors,
                            [name]: `Value must be less than or equal to ${currentValue}`,
                        });
                    }
                } else {
                    // Handle case where input length exceeds the allowed length
                    setErrors({
                        ...errors,
                        [name]: `Maximum allowed length is ${maxLength} digits`,
                    });
                }

            } else {
                // For entries in approved_other_details
                const index = parseInt(name.replace('approved_other_', ''), 10);
                const currentOther = i.imprest_details?.other_details[index];
                const currentValue = currentOther.amount !== undefined && currentOther.amount !== null ? currentOther.amount : 0;
                const maxLength = getNumberLength(currentValue);

                // Ensure the input value does not exceed the allowed length
                if (value.length <= maxLength) {
                    const numericValue = value.trim() ? parseInt(value, 10) : 0;

                    // Check if the numeric value is less than or equal to the current value
                    if (numericValue <= currentValue) {
                        const newOtherDetails = [...formData.other_details];
                        newOtherDetails[index].approved_other = value;

                        setInputState({
                            ...inputState,
                            [name]: value.trim() ? "success" : "",
                        });

                        setFormData({
                            ...formData,
                            other_details: newOtherDetails,
                        });

                        // Clear any previous error
                        setErrors({
                            ...errors,
                            [name]: "",
                        });
                    } else {
                        // Handle case where the numeric value exceeds the current value
                        setErrors({
                            ...errors,
                            [name]: `Value must be less than or equal to ${currentValue}`,
                        });
                    }
                } else {
                    // Handle case where input length exceeds the allowed length
                    setErrors({
                        ...errors,
                        [name]: `Maximum allowed length is ${maxLength} digits`,
                    });
                }
            }

        } else {
            // For textarea or other fields without length validation
            setInputState({
                ...inputState,
                [name]: value.trim() ? "success" : "",
            });

            setFormData({
                ...formData,
                [name]: value,
            });
            setErrors({
                ...errors,
                [name]: "",
            });
        }
    };

    const handleApprovedProjectImprestChange = (e, maxAmount) => {
        let { value } = e.target;

        // allow empty value (for backspace)
        if (value === "") {
            setFormData(prev => ({
                ...prev,
                approved_project_imprest_monthly: ""
            }));
            return;
        }

        value = Number(value);

        // prevent negative values
        if (value < 0) return;

        // restrict to max allowed amount
        if (value > maxAmount) {
            value = maxAmount;
        }

        setFormData(prev => ({
            ...prev,
            approved_project_imprest_monthly: value
        }));

        // optional: success/error state handling
        setInputState(prev => ({
            ...prev,
            approved_project_imprest_monthly: value > 0 ? "green" : ""
        }));

        setErrors(prev => ({
            ...prev,
            approved_project_imprest_monthly: ""
        }));
    };


    function getNumberLength(value) {
        return value?.toString()?.length;
    }

    const totalApproved =
        Number(formData.approved_stationary_item) +
        Number(formData.approved_electronic_item) +
        Number(formData.approved_grocery) +
        Number(formData.approved_project_imprest_monthly) +
        (formData.other_details?.length > 0
            ? formData.other_details?.reduce((total, other) => total + Number(other.approved_other || 0), 0)
            : 0);

    const approverLabel = i.employee ? (i.role ? `${i.employee?.name} (${i.role?.role_name})` : i.employee?.name) : i.role?.role_name;

    const handleFormSubmitPut = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            setShowProgressModal(true);
            setLoading(true);
            setUploadProgress(0);
            const loadingToastId = toast.loading("Loading: Please wait...");

            let progress = 0;
            const interval = setInterval(() => {
                progress += 10;
                if (progress >= 100) {
                    progress = 100;
                    clearInterval(interval);
                }
                setUploadProgress(progress);
            }, 100);

            let ImprestFormData = {
                amount_change_reason: formData.amount_change_reason,
                approved_stationary_item: formData.approved_stationary_item,
                approved_electronic_item: formData.approved_electronic_item,
                approved_grocery: formData.approved_grocery,
                other_details: formData.other_details,
                approved_project_imprest_monthly: formData.approved_project_imprest_monthly,
            };

            let ApprovalFormData = {
                approval_status: 'approved',
                updated_at: new Date().toISOString(),
                updated_by: sessionStorage.getItem('sitemanagement_emp_id')
            };

            try {
                let imp = await axios.put(`${BASE_URL}/siteproject/imprest/${i.imprest_details?.id}/`,
                    ImprestFormData,
                );

                if (imp.status === 200 || imp.status === 201) {
                    let req = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${i.id}/`, ApprovalFormData,);

                    if (req.status === 200 || req.status === 201) {
                        clearInterval(interval);
                        toast.dismiss(loadingToastId);
                        toast.success("Imprest Request Status Updated Successfully");
                        await getDynamicRequestData();
                        handleClose();
                    } else {
                        clearInterval(interval);
                        setUploadProgress(0);
                        toast.error(`Imprest Request Update Error: ${req.statusText}`);
                    }
                } else {
                    clearInterval(interval);
                    setUploadProgress(0);
                    toast.error(`Imprest Update Error: ${imp.statusText}`);
                }
            } catch (err) {
                clearInterval(interval);
                setUploadProgress(0);
                handleErrorToast(err, loadingToastId);
            } finally {
                clearInterval(interval);
                setTimeout(() => {
                    setShowProgressModal(false);
                }, 1500);
                setLoading(false);
                toast.dismiss(loadingToastId);
            }
        }
    };

    return (
        <>
            <style>{`
                .diae-info-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 12px 20px;
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 14px 18px;
                    margin-bottom: 18px;
                }
                .diae-info-item {
                    display: flex;
                    flex-direction: column;
                    gap: 3px;
                }
                .diae-info-label {
                    font-size: 11px;
                    font-weight: 600;
                    color: #94a3b8;
                    text-transform: uppercase;
                    letter-spacing: 0.4px;
                }
                .diae-info-value {
                    font-size: 13.5px;
                    color: #1f2937;
                    font-weight: 600;
                }
                .diae-amount-card {
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 8px 18px;
                }
                .diae-amount-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 10px 0;
                    font-size: 13.5px;
                    color: #475569;
                    gap: 12px;
                }
                .diae-amount-row:not(:last-child) {
                    border-bottom: 1px dashed #e2e8f0;
                }
                .diae-amount-row input {
                    width: 130px;
                    text-align: right;
                }
                .diae-amount-row.total {
                    font-weight: 700;
                    color: #1f2937;
                    font-size: 15px;
                    border-top: 1px solid #e2e8f0;
                    margin-top: 4px;
                    padding-top: 12px;
                }
            `}</style>

            <button className="model-delete-button-modern actionn-icon" style={{ backgroundColor: "#00000075" }} title={`Imprest Stage ${i.level} Editable Approval`} onClick={handleShow}>
                <LucideFileWarning color='white' size={20} />
            </button>

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Update" />
            ) : (
                <Modal show={show} onHide={handleClose} dialogClassName="modern-project-modal" size="lg">
                    <Modal.Header className="modern-modal-header">
                        <div className="header-content">
                            <div style={{ color: "white" }}>
                                <Edit3 size={24} />
                            </div>
                            <div>
                                <Modal.Title>Edit Stage {i.level} Approvable Amount</Modal.Title>
                                <p className="header-subtitle" style={{ color: "white" }}>
                                    {approverLabel} • Imprest "{i.imprest_details?.imprest_name}"
                                </p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={handleClose}>
                            <X size={20} />
                        </button>
                    </Modal.Header>

                    <Modal.Body className="modern-modal-body">
                        <div className="diae-info-grid">
                            <div className="diae-info-item">
                                <span className="diae-info-label">Project Code</span>
                                <span className="diae-info-value">{i.imprest_details?.project_code}</span>
                            </div>
                            <div className="diae-info-item">
                                <span className="diae-info-label">Project Name</span>
                                <span className="diae-info-value">{i.imprest_details?.project_short_name}</span>
                            </div>
                            <div className="diae-info-item">
                                <span className="diae-info-label">Date</span>
                                <span className="diae-info-value">{formattedDateLong(i.imprest_details?.date)}</span>
                            </div>
                            <div className="diae-info-item">
                                <span className="diae-info-label">Days</span>
                                <span className="diae-info-value">{i.imprest_details?.no_of_days}</span>
                            </div>
                        </div>

                        <div className="section-title">
                            <Wallet size={18} />
                            <h4>Approved Amount<span className="required" style={{ fontSize: '11px', textTransform: 'none' }}> (Editable, capped at requested amount)</span></h4>
                        </div>

                        <div className="diae-amount-card">
                            {i.imprest_details?.fixed_imprest_amount > 0 && (
                                <div className="diae-amount-row">
                                    <span>Monthly Imprest</span>
                                    <input
                                        id="approved_project_imprest_monthly"
                                        type="number"
                                        name="approved_project_imprest_monthly"
                                        placeholder="0"
                                        min={0}
                                        max={i.imprest_details.fixed_imprest_amount}
                                        onWheel={(e) => e.target.blur()}
                                        onChange={(e) => handleApprovedProjectImprestChange(e, i.imprest_details.fixed_imprest_amount)}
                                        value={formData.approved_project_imprest_monthly}
                                        className={errors.approved_project_imprest_monthly ? "error" : inputState.approved_project_imprest_monthly ? "success" : ""}
                                    />
                                </div>
                            )}
                            {errors.approved_project_imprest_monthly && <span className="error-message">{errors.approved_project_imprest_monthly}</span>}

                            {i.imprest_details?.stationary_item > 0 && (
                                <div className="diae-amount-row">
                                    <span>Stationary</span>
                                    <input
                                        id="approved_stationary_item"
                                        type="number"
                                        name="approved_stationary_item"
                                        placeholder="0"
                                        min={0}
                                        maxLength={getNumberLength(i.imprest_details?.stationary_item)}
                                        max={i.imprest_details.stationary_item}
                                        onWheel={(e) => e.target.blur()}
                                        onChange={handleInputChange}
                                        value={formData.approved_stationary_item}
                                        className={errors.approved_stationary_item ? "error" : inputState.approved_stationary_item ? "success" : ""}
                                    />
                                </div>
                            )}
                            {errors.approved_stationary_item && <span className="error-message">{errors.approved_stationary_item}</span>}

                            {i.imprest_details?.electronic_item > 0 && (
                                <div className="diae-amount-row">
                                    <span>Electronics</span>
                                    <input
                                        id="approved_electronic_item"
                                        type="number"
                                        name="approved_electronic_item"
                                        placeholder="0"
                                        min={0}
                                        max={i.imprest_details.electronic_item}
                                        maxLength={getNumberLength(i.imprest_details.electronic_item)}
                                        onWheel={(e) => e.target.blur()}
                                        onChange={handleInputChange}
                                        value={formData.approved_electronic_item}
                                        className={errors.approved_electronic_item ? "error" : inputState.approved_electronic_item ? "success" : ""}
                                    />
                                </div>
                            )}
                            {errors.approved_electronic_item && <span className="error-message">{errors.approved_electronic_item}</span>}

                            {i.imprest_details?.grocery > 0 && (
                                <div className="diae-amount-row">
                                    <span>Grocery</span>
                                    <input
                                        id="approved_grocery"
                                        type="number"
                                        name="approved_grocery"
                                        placeholder="0"
                                        min={0}
                                        max={i.imprest_details.approved_grocery}
                                        maxLength={getNumberLength(i.imprest_details.grocery)}
                                        onWheel={(e) => e.target.blur()}
                                        onChange={handleInputChange}
                                        value={formData.approved_grocery}
                                        className={errors.approved_grocery ? "error" : inputState.approved_grocery ? "success" : ""}
                                    />
                                </div>
                            )}
                            {errors.approved_grocery && <span className="error-message">{errors.approved_grocery}</span>}

                            {i.imprest_details?.other_details?.length > 0 && i.imprest_details.other_details.some(other => other.amount > 0) && (
                                formData.other_details?.map((other, index) => (
                                    i.imprest_details.other_details[index].amount > 0 && (
                                        <React.Fragment key={index}>
                                            <div className="diae-amount-row">
                                                <span>{other.other_name ? other.other_name : `Other-${index + 1}`}</span>
                                                <input
                                                    id={`approved_other_${index}`}
                                                    type="number"
                                                    name={`approved_other_${index}`}
                                                    placeholder="0"
                                                    min={0}
                                                    max={i.imprest_details.other_details[index].amount}
                                                    onWheel={(e) => e.target.blur()}
                                                    onChange={handleInputChange}
                                                    value={other.approved_other}
                                                    className={errors[`approved_other_${index}`] ? "error" : inputState[`approved_other_${index}`] ? "success" : ""}
                                                />
                                            </div>
                                            {errors[`approved_other_${index}`] && <span className="error-message">{errors[`approved_other_${index}`]}</span>}
                                        </React.Fragment>
                                    )
                                ))
                            )}

                            <div className="diae-amount-row total">
                                <span>Total Approved Amount</span>
                                <span>{formatCurrencyIndian(totalApproved)}</span>
                            </div>
                        </div>

                        <div className="form-field full-width" style={{ marginTop: '18px' }}>
                            <label> Amount Change Reason<span className="required">*</span></label>
                            <textarea
                                id="amount_change_reason"
                                name="amount_change_reason"
                                placeholder="Your Reason"
                                onChange={handleInputChange}
                                maxLength={100}
                                value={formData.amount_change_reason}
                                rows={2}
                                className={errors.amount_change_reason ? "error" : inputState.amount_change_reason ? "success" : ""}
                            />
                            {errors.amount_change_reason && <span className="error-message">{errors.amount_change_reason}</span>}
                        </div>

                        <div className="form-actions">
                            <button type="button" className="btn-secondaryy" onClick={handleClose}>
                                Cancel
                            </button>
                            <button type="submit" className="btn-primaryy" onClick={handleFormSubmitPut} disabled={loading}>
                                {loading ? <Loader size={18} className="spinning" /> : <Save size={18} />}
                                <span>{loading ? 'Submitting...' : 'Submit'}</span>
                            </button>
                        </div>

                        <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};

const DynamicRequestImprestFinalize = ({ i, getDynamicRequestData }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => {
        setFormData({
            approved_project_imprest_monthly: i.imprest_details?.approved_project_imprest_monthly ? i.imprest_details?.approved_project_imprest_monthly : i.imprest_details?.fixed_imprest_amount,

            approved_stationary_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_stationary_item : i.imprest_details?.stationary_item,
            approved_electronic_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_electronic_item : i.imprest_details?.electronic_item,
            approved_grocery: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_grocery : i.imprest_details?.grocery,
            other_details: i.imprest_details?.other_details?.map((other) => ({
                other_name: other.other_name,
                amount: other.amount,
                approved_other: i.imprest_details?.change_log?.length > 1 ? other.approved_other : other.amount,
            })),

            account_approved_final_details: "",
            account_transaction_proof: null,
            imprest_open: true,
        });
        setErrors({});
        setInputState({});
        setShow(true);
    };

    const [fileName, setFileName] = useState("");
    const [formData, setFormData] = useState({
        approved_project_imprest_monthly: i.imprest_details?.approved_project_imprest_monthly ? i.imprest_details?.approved_project_imprest_monthly : i.imprest_details?.fixed_imprest_amount,

        approved_stationary_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_stationary_item : i.imprest_details?.stationary_item,
        approved_electronic_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_electronic_item : i.imprest_details?.electronic_item,
        approved_grocery: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_grocery : i.imprest_details?.grocery,
        other_details: i.imprest_details?.other_details?.map((other) => ({
            other_name: other.other_name,
            amount: other.amount,
            approved_other: i.imprest_details?.change_log?.length > 1 ? other.approved_other : other.amount,
        })),

        account_approved_final_details: "",
        account_transaction_proof: null,
        imprest_open: true,
    });

    const totalApproved =
        i.imprest_details?.change_log?.length > 1
            ?
            (Number(i.imprest_details?.approved_stationary_item) +
                Number(i.imprest_details?.approved_electronic_item) +
                Number(i.imprest_details?.approved_grocery) +
                (i.imprest_details?.other_details?.length > 0
                    ? i.imprest_details?.other_details?.reduce((total, other) => total + Number(other.approved_other), 0)
                    : 0))
            : (
                Number(formData.approved_stationary_item) +
                Number(formData.approved_electronic_item) +
                Number(formData.approved_grocery) +
                (formData.other_details?.length > 0
                    ? formData.other_details?.reduce((total, other) => total + Number(other.approved_other || 0), 0)
                    : 0)
            );

    const totalApprovedWithMonthly =
        Number(formData.approved_stationary_item) +
        Number(formData.approved_electronic_item) +
        Number(formData.approved_grocery) +
        Number(formData.approved_project_imprest_monthly) +
        (formData.other_details?.length > 0
            ? formData.other_details?.reduce((total, other) => total + Number(other.approved_other || 0), 0)
            : 0);

    const approverLabel = i.employee ? (i.role ? `${i.employee?.name} (${i.role?.role_name})` : i.employee?.name) : i.role?.role_name;

    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});
    const validateForm = () => {
        const newErrors = {};
        const requiredFields =
            totalApproved === 0 ? [] :
                [
                    "account_approved_final_details",
                    "account_transaction_proof"
                ];
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setInputState({
            ...inputState,
            [name]: value.trim() ? "success" : "",
        });

        setFormData({
            ...formData,
            [name]: value,
        });

        setErrors({
            ...errors,
            [name]: "",
        });
    };

    const handleFileChange = (e) => {
        const { name } = e.target;
        const file = e.target.files[0];

        if (file) {
            setFormData({ ...formData, [name]: file });
            setFileName(file.name);
            setInputState({ ...inputState, [name]: "success" });
            setErrors({ ...errors, [name]: "" });
        } else {
            setFileName("");
        }
    };

    const handleClear = () => {
        setFormData(prevState => ({
            ...prevState,
            account_transaction_proof: null
        }));
        setFileName("");
        setInputState((prev) => ({ ...prev, account_transaction_proof: "" }));
    };

    const handleFormSubmitPut = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            setShowProgressModal(true);
            setLoading(true);
            setUploadProgress(0);
            const loadingToastId = toast.loading("Loading: Please wait...");

            let progress = 0;
            const interval = setInterval(() => {
                progress += 10;
                if (progress >= 100) {
                    progress = 100;
                    clearInterval(interval);
                }
                setUploadProgress(progress);
            }, 100);

            const ImprestFormData = new FormData();

            const symbols = '!@#$%^&*()_-+=';
            const lowercaseLetters = 'abcdefghijklmnopqrstuvwxyz';
            const uppercaseLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
            const numbers = '0123456789';
            const now = new Date();
            const date = now.toLocaleDateString('en-GB').split('/').reverse().join('-'); // Format: YYYY-MM-DD
            const time = now.toLocaleTimeString('en-GB', { hour12: false }).replace(/:/g, '-'); // Format: HH-MM-SS
            const allChars = symbols + lowercaseLetters + uppercaseLetters + numbers;
            let randomCode = '';
            for (let i = 0; i < 8; i++) {
                const randomIndex = Math.floor(Math.random() * allChars.length);
                randomCode += allChars[randomIndex];
            }
            if (formData.account_transaction_proof && formData.account_approved_final_details) {
                const originalFile = formData?.account_transaction_proof;
                const customFileName = `Aimantra ${date} ${time}_${randomCode} ${originalFile?.name}`;
                const customFile = new File([originalFile], customFileName, { type: originalFile.type });

                ImprestFormData.append("account_approved_final_details", formData.account_approved_final_details);
                ImprestFormData.append("account_transaction_proof", customFile);
            }
            ImprestFormData.append("imprest_open", formData.imprest_open);

            let ApprovalFormData = {
                approval_status: 'approved',
                updated_at: new Date().toISOString(),
                updated_by: sessionStorage.getItem('sitemanagement_emp_id')
            };

            try {
                let imp = await axios.put(`${BASE_URL}/siteproject/imprest/${i.imprest_details?.id}/`, ImprestFormData, AUTHH, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                });

                if (imp.status === 200 || imp.status === 201) {
                    let req = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${i.id}/`, ApprovalFormData,);

                    if (req.status === 200 || req.status === 201) {
                        clearInterval(interval);
                        toast.dismiss(loadingToastId);
                        toast.success("Imprest Request Status Updated Successfully");
                        await getDynamicRequestData();
                        handleClose();
                    } else {
                        clearInterval(interval);
                        setUploadProgress(0);
                        toast.error(`Imprest Request Update Error: ${req.statusText}`);
                        console.log('Imprest Request Update Error:');
                        console.error(req);
                    }
                } else {
                    clearInterval(interval);
                    setUploadProgress(0);
                    toast.error(`Imprest Update Error: ${imp.statusText}`);
                    console.log('Imprest Update Error:');
                    console.error(imp);
                }
            } catch (err) {
                clearInterval(interval);
                setUploadProgress(0);
                handleErrorToast(err, loadingToastId);
            } finally {
                clearInterval(interval);
                setTimeout(() => {
                    setShowProgressModal(false);
                }, 1500);
                setLoading(false);
                toast.dismiss(loadingToastId);
            }
        }
    };

    return (
        <>
            <style>{`
                .dif-info-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 12px 20px;
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 14px 18px;
                    margin-bottom: 18px;
                }
                .dif-info-item {
                    display: flex;
                    flex-direction: column;
                    gap: 3px;
                }
                .dif-info-label {
                    font-size: 11px;
                    font-weight: 600;
                    color: #94a3b8;
                    text-transform: uppercase;
                    letter-spacing: 0.4px;
                }
                .dif-info-value {
                    font-size: 13.5px;
                    color: #1f2937;
                    font-weight: 600;
                }
                .dif-amount-card {
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 8px 18px;
                }
                .dif-amount-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 10px 0;
                    font-size: 13.5px;
                    color: #475569;
                    gap: 12px;
                }
                .dif-amount-row:not(:last-child) {
                    border-bottom: 1px dashed #e2e8f0;
                }
                .dif-amount-row .dif-value {
                    font-weight: 600;
                    color: #1f2937;
                }
                .dif-amount-row.total {
                    font-weight: 700;
                    color: #1f2937;
                    font-size: 15px;
                    border-top: 1px solid #e2e8f0;
                    margin-top: 4px;
                    padding-top: 12px;
                }
                .dif-file-row {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    margin-top: 6px;
                }
                .dif-file-upload {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 44px;
                    height: 44px;
                    border-radius: 10px;
                    cursor: pointer;
                }
                .dif-file-name {
                    font-size: 13px;
                    color: #475569;
                    margin: 0;
                }
            `}</style>

            <button className="model-edit-button" title={`Imprest Stage ${i.level} Finalize Approval`} onClick={handleShow}>
                <CheckGreen />
            </button>

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Add" />
            ) : (
                <Modal show={show} onHide={handleClose} dialogClassName="modern-project-modal" size="lg">
                    <Modal.Header className="modern-modal-header">
                        <div className="header-content">
                            <div style={{ color: "white" }}>
                                <CheckGreen size={24} />
                            </div>
                            <div>
                                <Modal.Title>Finalize Stage {i.level} Approval</Modal.Title>
                                <p className="header-subtitle" style={{ color: "white" }}>
                                    {approverLabel} • Imprest "{i.imprest_details?.imprest_name}"
                                </p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={handleClose}>
                            <X size={20} />
                        </button>
                    </Modal.Header>

                    <Modal.Body className="modern-modal-body">
                        <div className="dif-info-grid">
                            <div className="dif-info-item">
                                <span className="dif-info-label">Project Code</span>
                                <span className="dif-info-value">{i.imprest_details?.project_code}</span>
                            </div>
                            <div className="dif-info-item">
                                <span className="dif-info-label">Project Name</span>
                                <span className="dif-info-value">{i.imprest_details?.project_short_name}</span>
                            </div>
                            <div className="dif-info-item">
                                <span className="dif-info-label">Date</span>
                                <span className="dif-info-value">{formattedDateLong(i.imprest_details?.date)}</span>
                            </div>
                            <div className="dif-info-item">
                                <span className="dif-info-label">Days</span>
                                <span className="dif-info-value">{i.imprest_details?.no_of_days}</span>
                            </div>
                        </div>

                        <div className="section-title">
                            <Wallet size={18} />
                            <h4>Approved Amount</h4>
                        </div>

                        <div className="dif-amount-card">
                            {i.imprest_details?.fixed_imprest_amount > 0 && (
                                <div className="dif-amount-row">
                                    <span>Monthly Imprest</span>
                                    <span className="dif-value">{formatCurrencyIndian(formData.approved_project_imprest_monthly)}</span>
                                </div>
                            )}

                            {i.imprest_details?.stationary_item > 0 && (
                                <div className="dif-amount-row">
                                    <span>Stationary</span>
                                    <span className="dif-value">{formatCurrencyIndian(formData.approved_stationary_item)}</span>
                                </div>
                            )}

                            {i.imprest_details?.electronic_item > 0 && (
                                <div className="dif-amount-row">
                                    <span>Electronics</span>
                                    <span className="dif-value">{formatCurrencyIndian(formData.approved_electronic_item)}</span>
                                </div>
                            )}

                            {i.imprest_details?.grocery > 0 && (
                                <div className="dif-amount-row">
                                    <span>Grocery</span>
                                    <span className="dif-value">{formatCurrencyIndian(formData.approved_grocery)}</span>
                                </div>
                            )}

                            {i.imprest_details?.other_details?.length > 0 && i.imprest_details.other_details.some(other => other.amount > 0) && (
                                formData.other_details?.map((other, index) => (
                                    i.imprest_details.other_details[index].amount > 0 && (
                                        <div className="dif-amount-row" key={index}>
                                            <span className="form-text-trasformation-uppercase">
                                                {other.other_name ? other.other_name : `Other-${index + 1}`}
                                            </span>
                                            <span className="dif-value">{formatCurrencyIndian(other.approved_other)}</span>
                                        </div>
                                    )
                                ))
                            )}

                            <div className="dif-amount-row total">
                                <span>Total Approved Amount</span>
                                <span>{formatCurrencyIndian(totalApprovedWithMonthly)}</span>
                            </div>
                        </div>

                        {totalApproved === 0 ? null : (
                            <>
                                <div className="form-field full-width" style={{ marginTop: '18px' }}>
                                    <label htmlFor="account_approved_final_details">
                                        Final Approval Details (Transaction / Cheque no.)<span className="required">*</span>
                                    </label>
                                    <input
                                        id="account_approved_final_details"
                                        name="account_approved_final_details"
                                        placeholder="Final Approval Details (Transaction / Cheque no.)"
                                        onChange={handleInputChange}
                                        value={formData.account_approved_final_details}
                                        className={errors.account_approved_final_details ? "error" : inputState.account_approved_final_details ? "success" : ""}
                                    />
                                    {errors.account_approved_final_details && (
                                        <span className="error-message">{errors.account_approved_final_details}</span>
                                    )}
                                </div>

                                <div className="form-field full-width" style={{ marginTop: '14px' }}>
                                    <label>Account Transaction Proof<span className="required">*</span></label>
                                    <div className="dif-file-row">
                                        <label
                                            htmlFor="account_transaction_proof"
                                            className={`dif-file-upload ${errors.account_transaction_proof
                                                ? "error"
                                                : inputState.account_transaction_proof
                                                    ? "success"
                                                    : ""
                                                }`}
                                        >
                                            <Attachment />
                                        </label>
                                        <input
                                            id="account_transaction_proof"
                                            name="account_transaction_proof"
                                            type="file"
                                            onChange={handleFileChange}
                                            onFocus={handleClear}
                                            style={{ display: "none" }}
                                        />
                                        {fileName && <p className="dif-file-name">{fileName}</p>}
                                        {formData.account_transaction_proof && (
                                            <button type="button" className="btn-secondaryy" onClick={handleClear}>
                                                Clear
                                            </button>
                                        )}
                                    </div>
                                    {errors.account_transaction_proof && (
                                        <span className="error-message">{errors.account_transaction_proof}</span>
                                    )}
                                </div>
                            </>
                        )}

                        <div className="form-actions">
                            <button type="button" className="btn-secondaryy" onClick={handleClose}>
                                Cancel
                            </button>
                            <button type="submit" className="btn-primaryy" onClick={handleFormSubmitPut} disabled={loading}>
                                {loading ? <Loader size={18} className="spinning" /> : <Save size={18} />}
                                <span>{loading ? 'Submitting...' : 'Confirm'}</span>
                            </button>
                        </div>

                        <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};

const DynamicRequestImprestRejection = ({ i, getDynamicRequestData }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => {
        setFormData({
            approved_project_imprest_monthly: i.imprest_details?.fixed_imprest_amount,

            approved_stationary_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_stationary_item : i.imprest_details?.stationary_item,
            approved_electronic_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_electronic_item : i.imprest_details?.electronic_item,
            approved_grocery: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_grocery : i.imprest_details?.grocery,
            other_details: i.imprest_details?.other_details?.map((other) => ({
                other_name: other.other_name,
                amount: other.amount,
                approved_other: i.imprest_details?.change_log?.length > 1 ? other.approved_other : other.amount,
            })),
            remarks: "",
        });
        setErrors({});
        setInputState({});
        setShow(true);
    };

    const [formData, setFormData] = useState({
        approved_project_imprest_monthly: i.imprest_details?.fixed_imprest_amount,

        approved_stationary_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_stationary_item : i.imprest_details?.stationary_item,
        approved_electronic_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_electronic_item : i.imprest_details?.electronic_item,
        approved_grocery: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_grocery : i.imprest_details?.grocery,
        other_details: i.imprest_details?.other_details?.map((other) => ({
            other_name: other.other_name,
            amount: other.amount,
            approved_other: i.imprest_details?.change_log?.length > 1 ? other.approved_other : other.amount,
        })),
        remarks: "",
    });

    //******************Validation Start********************* */

    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ["remarks"];
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setInputState({
            ...inputState,
            [name]: value.trim() ? "success" : "",
        });

        setFormData({
            ...formData,
            [name]: value,
        });

        setErrors({
            ...errors,
            [name]: "",
        });
    };

    const totalApproved =
        Number(formData.approved_stationary_item) +
        Number(formData.approved_electronic_item) +
        Number(formData.approved_grocery) +
        Number(formData.approved_project_imprest_monthly) +
        (formData.other_details?.length > 0
            ? formData.other_details?.reduce((total, other) => total + Number(other.approved_other || 0), 0)
            : 0);

    const approverLabel = i.employee ? (i.role ? `${i.employee?.name} (${i.role?.role_name})` : i.employee?.name) : i.role?.role_name;

    const handleFormSubmitPut = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            setShowProgressModal(true);
            setLoading(true);
            setUploadProgress(0);
            const loadingToastId = toast.loading("Loading: Please wait...");

            let progress = 0;
            const interval = setInterval(() => {
                progress += 10;
                if (progress >= 100) {
                    progress = 100;
                    clearInterval(interval);
                }
                setUploadProgress(progress);
            }, 100);

            let RejectedStatus = {
                approval_status: 'rejected',
                remarks: formData.remarks,
                updated_at: new Date().toISOString(),
                updated_by: sessionStorage.getItem('sitemanagement_emp_id')
            };

            try {
                let req = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${i.id}/`, RejectedStatus);

                if (req.status === 200 || req.status === 201) {
                    clearInterval(interval);
                    toast.dismiss(loadingToastId);
                    toast.success("Imprest Request Status Updated Successfully");
                    await getDynamicRequestData();
                    handleClose();
                } else {
                    clearInterval(interval);
                    setUploadProgress(0);
                    toast.error(`Imprest Request Update Error: ${req.statusText}`);
                    console.log('Imprest Request Update Error:');
                    console.error(req);
                }
            } catch (err) {
                clearInterval(interval);
                setUploadProgress(0);
                handleErrorToast(err, loadingToastId);
            } finally {
                clearInterval(interval);
                setTimeout(() => {
                    setShowProgressModal(false);
                }, 1500);
                setLoading(false);
                toast.dismiss(loadingToastId);
            }
        }
    };

    return (
        <>
            <style>{`
                .dir-info-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 12px 20px;
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 14px 18px;
                    margin-bottom: 18px;
                }
                .dir-info-item {
                    display: flex;
                    flex-direction: column;
                    gap: 3px;
                }
                .dir-info-label {
                    font-size: 11px;
                    font-weight: 600;
                    color: #94a3b8;
                    text-transform: uppercase;
                    letter-spacing: 0.4px;
                }
                .dir-info-value {
                    font-size: 13.5px;
                    color: #1f2937;
                    font-weight: 600;
                }
                .dir-amount-card {
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 8px 18px;
                }
                .dir-amount-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 10px 0;
                    font-size: 13.5px;
                    color: #475569;
                    gap: 12px;
                }
                .dir-amount-row:not(:last-child) {
                    border-bottom: 1px dashed #e2e8f0;
                }
                .dir-amount-row .dir-value {
                    font-weight: 600;
                    color: #1f2937;
                }
                .dir-amount-row.total {
                    font-weight: 700;
                    color: #1f2937;
                    font-size: 15px;
                    border-top: 1px solid #e2e8f0;
                    margin-top: 4px;
                    padding-top: 12px;
                }
            `}</style>

            <button className="model-delete-button-modern actionn-icon" style={{ backgroundColor: "#00000075" }} title={`Imprest Stage ${i.level} Rejection`} onClick={handleShow}>
                <X size={20} color='white' />
            </button>

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Reject" />
            ) : (
                <Modal show={show} onHide={handleClose} dialogClassName="modern-project-modal" size="lg">
                    <Modal.Header className="modern-modal-header">
                        <div className="header-content">
                            <div style={{ color: "white" }}>
                                <CrossRed size={24} />
                            </div>
                            <div>
                                <Modal.Title>Reject Stage {i.level} Approval</Modal.Title>
                                <p className="header-subtitle" style={{ color: "white" }}>
                                    {approverLabel} • Imprest "{i.imprest_details?.imprest_name}"
                                </p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={handleClose}>
                            <X size={20} />
                        </button>
                    </Modal.Header>

                    <Modal.Body className="modern-modal-body">
                        <div className="dir-info-grid">
                            <div className="dir-info-item">
                                <span className="dir-info-label">Project Code</span>
                                <span className="dir-info-value">{i.imprest_details?.project_code}</span>
                            </div>
                            <div className="dir-info-item">
                                <span className="dir-info-label">Project Name</span>
                                <span className="dir-info-value">{i.imprest_details?.project_short_name}</span>
                            </div>
                            <div className="dir-info-item">
                                <span className="dir-info-label">Date</span>
                                <span className="dir-info-value">{formattedDateLong(i.imprest_details?.date)}</span>
                            </div>
                            <div className="dir-info-item">
                                <span className="dir-info-label">Days</span>
                                <span className="dir-info-value">{i.imprest_details?.no_of_days}</span>
                            </div>
                        </div>

                        <div className="section-title">
                            <Wallet size={18} />
                            <h4>Approved Amount</h4>
                        </div>

                        <div className="dir-amount-card">
                            {i.imprest_details?.fixed_imprest_amount > 0 && (
                                <div className="dir-amount-row">
                                    <span>Monthly Imprest</span>
                                    <span className="dir-value">{formatCurrencyIndian(formData.approved_project_imprest_monthly)}</span>
                                </div>
                            )}

                            {i.imprest_details?.stationary_item > 0 && (
                                <div className="dir-amount-row">
                                    <span>Stationary</span>
                                    <span className="dir-value">{formatCurrencyIndian(formData.approved_stationary_item)}</span>
                                </div>
                            )}

                            {i.imprest_details?.electronic_item > 0 && (
                                <div className="dir-amount-row">
                                    <span>Electronics</span>
                                    <span className="dir-value">{formatCurrencyIndian(formData.approved_electronic_item)}</span>
                                </div>
                            )}

                            {i.imprest_details?.grocery > 0 && (
                                <div className="dir-amount-row">
                                    <span>Grocery</span>
                                    <span className="dir-value">{formatCurrencyIndian(formData.approved_grocery)}</span>
                                </div>
                            )}

                            {i.imprest_details?.other_details?.length > 0 && i.imprest_details.other_details.some(other => other.amount > 0) && (
                                formData.other_details?.map((other, index) => (
                                    i.imprest_details.other_details[index].amount > 0 && (
                                        <div className="dir-amount-row" key={index}>
                                            <span className="form-text-trasformation-uppercase">
                                                {other.other_name ? other.other_name : `Other-${index + 1}`}
                                            </span>
                                            <span className="dir-value">{formatCurrencyIndian(other.approved_other)}</span>
                                        </div>
                                    )
                                ))
                            )}

                            <div className="dir-amount-row total">
                                <span>Total Approved Amount</span>
                                <span>{formatCurrencyIndian(totalApproved)}</span>
                            </div>
                        </div>

                        <div className="form-field full-width" style={{ marginTop: '18px' }}>
                            <label htmlFor="remarks">Rejection Reason<span className="required">*</span></label>
                            <textarea
                                id="remarks"
                                name="remarks"
                                placeholder="Your Reason"
                                onChange={handleInputChange}
                                maxLength={100}
                                value={formData.remarks}
                                rows={3}
                                className={errors.remarks ? "error" : inputState.remarks ? "success" : ""}
                            />
                            {errors.remarks && (
                                <span className="error-message">{errors.remarks}</span>
                            )}
                        </div>

                        <div className="form-actions">
                            <button type="button" className="btn-secondaryy" onClick={handleClose}>
                                Cancel
                            </button>
                            <button type="submit" className="btn-primaryy btn-danger" onClick={handleFormSubmitPut} disabled={loading}>
                                {loading ? <Loader size={18} className="spinning" /> : <X size={18} />}
                                <span>{loading ? 'Submitting...' : 'Reject'}</span>
                            </button>
                        </div>

                        <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};

const DynamicRequestExpenseApproval = ({ i, j, k, getExpenseData, handleBackRoute }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const sum = j?.some(expense => expense.bill_approved_amt === null)
        ? j?.reduce((acc, item) => acc + parseFloat(item.item_amount || 0), 0)
        : j?.reduce((acc, item) => acc + parseFloat(item.bill_approved_amt || 0), 0);

    const total =
        Number(i.imprest_data?.approved_stationary_item || 0) +
        Number(i.imprest_data?.approved_electronic_item || 0) +
        Number(i.imprest_data?.approved_project_imprest_monthly || 0) +
        Number(i.imprest_data?.approved_grocery || 0) +

        Number(i.imprest_data?.other_details?.length > 0
            ? i.imprest_data?.other_details?.reduce((total, other) => total + Number(other.approved_other || 0), 0)
            : 0)

    const remaining = total - sum;

    const approverLabel = k.employee ? (k.role ? `${k.employee?.name} (${k.role?.role_name})` : k.employee?.name) : k.role?.role_name;

    const handleDynamicApproval = async (e) => {
        e.preventDefault();
        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(0);

        const loadingToastId = toast.loading("Loading: Please wait...");

        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            if (progress >= 100) {
                progress = 100;
                clearInterval(interval);
            }
            setUploadProgress(progress);
        }, 100);

        let ApprovalFormData = {
            approval_status: 'approved',
            updated_at: new Date().toISOString(),
            updated_by: sessionStorage.getItem('sitemanagement_emp_id')
        };

        try {

            let exp = await axios.put(`${BASE_URL}/siteproject/expense/${i.id}/`, {
                partially_change: false,
                utilized_amount: sum,
                remaining_amount: remaining,
            });

            if (exp.status === 200 || exp.status === 201) {
                let req = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${k.id}/`, ApprovalFormData,);
                if (req.status === 200 || req.status === 201) {
                    clearInterval(interval);
                    toast.dismiss(loadingToastId);
                    toast.success("Expense Request Approved Successfully");
                    handleClose()
                    // await getExpenseData();
                    toast.info("Navigating to Expense Requests Page");
                    handleBackRoute()
                } else {
                    clearInterval(interval);
                    setUploadProgress(0);
                    toast.error(`Expense Request Update Error: ${req.statusText}`);
                }
            } else {
                clearInterval(interval);
                setUploadProgress(0);
                toast.error(`Expense Update Error: ${exp.statusText}`);
            }
        } catch (err) {
            clearInterval(interval);
            setUploadProgress(0);
            handleErrorToast(err, loadingToastId)
        } finally {
            clearInterval(interval);
            setTimeout(() => {
                setShowProgressModal(false);
            }, 1500);
            setLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    return (
        <>
            <style>{`
                .dea-info-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 12px 20px;
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 14px 18px;
                    margin-bottom: 18px;
                }
                .dea-info-item {
                    display: flex;
                    flex-direction: column;
                    gap: 3px;
                }
                .dea-info-label {
                    font-size: 11px;
                    font-weight: 600;
                    color: #94a3b8;
                    text-transform: uppercase;
                    letter-spacing: 0.4px;
                }
                .dea-info-value {
                    font-size: 13.5px;
                    color: #1f2937;
                    font-weight: 600;
                }
                .dea-amount-card {
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 14px 18px;
                }
                .dea-amount-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 7px 0;
                    font-size: 13.5px;
                    color: #475569;
                }
                .dea-amount-row:not(:last-child) {
                    border-bottom: 1px dashed #e2e8f0;
                }
                .dea-amount-row.total {
                    font-weight: 700;
                    color: #1f2937;
                    font-size: 15px;
                    padding-top: 10px;
                }
            `}</style>

            <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

            <button className="btn-primaryy" style={{ background: "#00000075", color: "white" }} onClick={handleShow}>
                Approve
            </button>

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Add" />
            ) : (
                <Modal show={show} onHide={handleClose} dialogClassName="modern-project-modal" size="lg">
                    <Modal.Header className="modern-modal-header">
                        <div className="header-content">
                            <div style={{ color: "white" }}>
                                <CheckCircle size={20} />
                            </div>
                            <div>
                                <Modal.Title>Confirm Stage {k.level} Approval</Modal.Title>
                                <p className="header-subtitle" style={{ color: "white" }}>
                                    {approverLabel} &bull; Expense "{i.name}"
                                </p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={handleClose}>
                            <X size={20} />
                        </button>
                    </Modal.Header>

                    <Modal.Body className="modern-modal-body">
                        <div className="dea-info-grid">
                            <div className="dea-info-item">
                                <span className="dea-info-label">Date</span>
                                <span className="dea-info-value">{formattedDateLong(i.date)}</span>
                            </div>
                            <div className="dea-info-item">
                                <span className="dea-info-label">Expense against Imprest</span>
                                <span className="dea-info-value">{i.project_name}</span>
                            </div>
                        </div>

                        <div className="section-title">
                            <Wallet size={18} />
                            <h4>Amount Breakdown</h4>
                        </div>

                        <div className="dea-amount-card">
                            <div className="dea-amount-row">
                                <span>Utilized Amount</span>
                                <span style={{ color: "#dc2626", fontWeight: 700 }}>{formatCurrencyIndian(sum)}</span>
                            </div>
                            <div className="dea-amount-row total">
                                <span>Remaining Amount</span>
                                <span style={{ color: "#2576bc" }}>{formatCurrencyIndian(remaining)}</span>
                            </div>
                        </div>

                        <div className="form-actions">
                            <button type="button" className="btn-primaryy" style={{ background: "#00000075" }} onClick={handleClose}>
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn-primaryy" style={{ background: "#00000075" }}
                                onClick={handleDynamicApproval}
                                disabled={loading}
                            >
                                {loading ? <Loader size={18} className="spinning" /> : <Check size={18} />}
                                <span>{loading ? 'Approving...' : 'Approve'}</span>
                            </button>
                        </div>
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};

const DynamicRequestExpensePartialEdit = ({ i, j, k, getExpenseData, handleBackRoute }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const sum = j?.some(expense => expense.bill_approved_amt === null)
        ? j?.reduce((acc, item) => acc + parseFloat(item.item_amount || 0), 0)
        : j?.reduce((acc, item) => acc + parseFloat(item.bill_approved_amt || 0), 0);

    const total =
        Number(i.imprest_data?.approved_stationary_item || 0) +
        Number(i.imprest_data?.approved_electronic_item || 0) +
        Number(i.imprest_data?.approved_grocery || 0) +
        Number(i.imprest_data?.approved_project_imprest_monthly || 0) +
        Number(i.imprest_data?.other_details?.length > 0
            ? i.imprest_data?.other_details?.reduce((total, other) => total + Number(other.approved_other || 0), 0)
            : 0);

    const remaining = total - sum;

    const approverLabel = k.employee ? (k.role ? `${k.employee?.name} (${k.role?.role_name})` : k.employee?.name) : k.role?.role_name;

    const runProgress = () => {
        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(0);

        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            if (progress >= 100) {
                progress = 100;
                clearInterval(interval);
            }
            setUploadProgress(progress);
        }, 100);

        return interval;
    };

    const finishProgress = (interval, loadingToastId) => {
        clearInterval(interval);
        setTimeout(() => {
            setShowProgressModal(false);
        }, 1500);
        setLoading(false);
        toast.dismiss(loadingToastId);
    };

    const handleDynamicEditRejected = async (e) => {
        e.preventDefault();
        const loadingToastId = toast.loading("Loading: Please wait...");
        const interval = runProgress();

        try {
            let res = await axios.put(`${BASE_URL}/siteproject/expense/${i.id}/`, {
                partially_change: false,
            });

            if (res.status === 200) {
                clearInterval(interval);
                toast.dismiss(loadingToastId);
                toast.success("Expense Updated Successfully");
                handleClose();
                // await getExpenseData();
                toast.info("Navigating to Expense Requests Page");
                handleBackRoute();
            } else {
                clearInterval(interval);
                setUploadProgress(0);
                toast.error(`Expense Update Error: ${res.statusText}`);
                console.log('Expense Update Error:');
                console.error(res);
            }
        } catch (err) {
            clearInterval(interval);
            setUploadProgress(0);
            handleErrorToast(err, loadingToastId);
        } finally {
            finishProgress(interval, loadingToastId);
        }
    };

    const handleDynamicEditApproval = async (e) => {
        e.preventDefault();
        const loadingToastId = toast.loading("Loading: Please wait...");
        const interval = runProgress();

        try {
            let res = await axios.put(`${BASE_URL}/siteproject/expense/${i.id}/`, {
                partially_change: true,
            });

            if (res.status === 200) {
                clearInterval(interval);
                toast.dismiss(loadingToastId);
                toast.success("Expense Updated Successfully");
                handleClose();
                // await getExpenseData();
                toast.info("Navigating to Expense Requests Page");
                handleBackRoute();
            } else {
                clearInterval(interval);
                setUploadProgress(0);
                toast.error(`Expense Update Error: ${res.statusText}`);
                console.log('Expense Update Error:');
                console.error(res);
            }
        } catch (err) {
            clearInterval(interval);
            setUploadProgress(0);
            handleErrorToast(err, loadingToastId);
        } finally {
            finishProgress(interval, loadingToastId);
        }
    };

    return (
        <>
            <style>{`
                .dea-info-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 12px 20px;
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 14px 18px;
                    margin-bottom: 18px;
                }
                .dea-info-item {
                    display: flex;
                    flex-direction: column;
                    gap: 3px;
                }
                .dea-info-label {
                    font-size: 11px;
                    font-weight: 600;
                    color: #94a3b8;
                    text-transform: uppercase;
                    letter-spacing: 0.4px;
                }
                .dea-info-value {
                    font-size: 13.5px;
                    color: #1f2937;
                    font-weight: 600;
                }
                .dea-amount-card {
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 14px 18px;
                }
                .dea-amount-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 7px 0;
                    font-size: 13.5px;
                    color: #475569;
                }
                .dea-amount-row:not(:last-child) {
                    border-bottom: 1px dashed #e2e8f0;
                }
                .dea-amount-row.total {
                    font-weight: 700;
                    color: #1f2937;
                    font-size: 15px;
                    padding-top: 10px;
                }
            `}</style>

            <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

            <button className="btn-primaryy" style={{ background: "#00000075", color: "white" }} title="Account A Expense Approve" onClick={handleShow}>
                Permit Bill Update
            </button>

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Update" />
            ) : (
                <Modal show={show} onHide={handleClose} dialogClassName="modern-project-modal" size="lg">
                    <Modal.Header className="modern-modal-header">
                        <div className="header-content">
                            <div style={{ color: "white" }}>
                                <Wallet size={20} />
                            </div>
                            <div>
                                <Modal.Title>Confirm Stage {k.level} Approval</Modal.Title>
                                <p className="header-subtitle" style={{ color: "white" }}>
                                    {approverLabel} &bull; Bill Modification for "{i.name}"
                                </p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={handleClose}>
                            <X size={20} />
                        </button>
                    </Modal.Header>

                    <Modal.Body className="modern-modal-body">
                        <div className="dea-info-grid">
                            <div className="dea-info-item">
                                <span className="dea-info-label">Date</span>
                                <span className="dea-info-value">{formattedDateLong(i.date)}</span>
                            </div>
                            <div className="dea-info-item">
                                <span className="dea-info-label">Expense against Imprest</span>
                                <span className="dea-info-value">{i.project_name}</span>
                            </div>
                            <div className="dea-info-item">
                                <span className="dea-info-label">Current No. of Bills</span>
                                <span className="dea-info-value">{j.length}</span>
                            </div>
                        </div>

                        <div className="section-title">
                            <Wallet size={18} />
                            <h4>Amount Breakdown</h4>
                        </div>

                        <div className="dea-amount-card">
                            <div className="dea-amount-row">
                                <span>Utilized Amount</span>
                                <span style={{ color: "#dc2626", fontWeight: 700 }}>{formatCurrencyIndian(sum)}</span>
                            </div>
                            <div className="dea-amount-row total">
                                <span>Remaining Amount</span>
                                <span style={{ color: "#2576bc" }}>{formatCurrencyIndian(remaining)}</span>
                            </div>
                        </div>

                        <div className="form-actions">
                            <button type="button" className="btn-primaryy" style={{ background: "#00000075" }} onClick={handleClose}>
                                Cancel
                            </button>
                            {i.partially_change ? (
                                <button
                                    type="submit"
                                    className="btn-primaryy btn-danger"
                                    onClick={handleDynamicEditRejected}
                                    disabled={loading}
                                >
                                    {loading ? <Loader size={18} className="spinning" /> : <X size={18} />}
                                    <span>{loading ? 'Locking...' : 'Lock Edit'}</span>
                                </button>
                            ) : (
                                <button
                                    type="submit"
                                    className="btn-primaryy"
                                    style={{ background: "#00000075" }}
                                    onClick={handleDynamicEditApproval}
                                    disabled={loading}
                                >
                                    {loading ? <Loader size={18} className="spinning" /> : <Check size={18} />}
                                    <span>{loading ? 'Unlocking...' : 'Unlock Edit'}</span>
                                </button>
                            )}
                        </div>
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};

const DynamicRequestExpenseFinalize = ({ i, j, getDynamicRequestData, handleBackRoute }) => {
    const [show, setShow] = useState(false);
    const handleShow = async () => {
        setFormData({
            decision: "",
            settlement: "",
            settlement_amount: "",
            settlement_bill: "",
        })
        setShow(true)
    };
    const handleClose = () => {
        setShow(false)
    };

    const [formData, setFormData] = useState({
        decision: "",
        settlement: "",
        settlement_amount: "",
        settlement_bill: "",
    });

    const [fileName, setFileName] = useState("");

    const handleFileChange = (e) => {
        const { name } = e.target;
        const file = e.target.files[0];

        if (file) {
            setFormData({ ...formData, [name]: file, });
            setFileName(file.name);
        } else {
            setFileName("");
        }
    };

    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = [
            "decision",
            formData.decision === 'Paid' || formData.decision === 'Salary' ? "settlement_amount" : null,
            formData.decision === 'Paid' ? "settlement_bill" : null,
        ].filter(Boolean);

        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const handleClear = () => {
        setFormData(prevState => ({
            ...prevState,
            // settlement_amount: "",
            settlement_bill: null,
        }));
        setFileName("");
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setInputState({
            ...inputState,
            [name]: value.trim() ? "green" : "",
        });

        // if (name === "decision" && value !== "Paid") {
        //   handleClear();
        // }

        setFormData({
            ...formData,
            [name]: value,
        });
    };
    const [loading, setLoading] = useState(false); //loading logic

    const handleAccountFinalize = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic
            setLoading(true); //loading logic

            const ExpenseFormData = new FormData();

            const symbols = '!@#$%^&*()_-+=';
            const lowercaseLetters = 'abcdefghijklmnopqrstuvwxyz';
            const uppercaseLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
            const numbers = '0123456789';
            const now = new Date();
            const date = now.toLocaleDateString('en-GB').split('/').reverse().join('-'); // Format: YYYY-MM-DD
            const time = now.toLocaleTimeString('en-GB', { hour12: false }).replace(/:/g, '-'); // Format: HH-MM-SS
            const allChars = symbols + lowercaseLetters + uppercaseLetters + numbers;
            let randomCode = '';
            for (let i = 0; i < 8; i++) {
                const randomIndex = Math.floor(Math.random() * allChars.length);
                randomCode += allChars[randomIndex];
            }

            if (formData.settlement_bill) {
                const originalFile = formData.settlement_bill;
                const customFileName = `Aimantra ${date} ${time}_${randomCode} ${originalFile.name}`;
                const customFile = new File([originalFile], customFileName, { type: originalFile.type });

                ExpenseFormData.append("settlement_bill", customFile);
                ExpenseFormData.append("settlement", true);
            }
            ExpenseFormData.append("decision", formData.decision);
            ExpenseFormData.append("partially_change", false);
            ExpenseFormData.append("settlement_amount", formData.settlement_amount);

            let ApprovalFormData = {
                approval_status: 'approved',
                updated_at: new Date().toISOString(),
                updated_by: sessionStorage.getItem('sitemanagement_emp_id')
            };

            try {
                let exp = await axios.put(`${BASE_URL}/siteproject/expense/${j.id}/`, ExpenseFormData);

                if (exp.status === 200) {
                    toast.success("Expense Updated Successfully");

                    let req = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${i.id}/`, ApprovalFormData,);

                    if (req.status === 200 || req.status === 201) {
                        toast.success("Expense Request Status Updated Successfully");
                        await getDynamicRequestData();
                    } else {
                        toast.error(`Expense Request Update Error: ${req.statusText}`);
                        console.log('Expense Request Update Error:')
                        console.error(req);

                    }
                } else {
                    toast.error(`Expense Update Error: ${exp.statusText}`);
                    console.log('Expense Update Error:')
                    console.error(exp);
                }
            } catch (err) {
                handleErrorToast(err, loadingToastId);
            } finally {
                setLoading(false); //loading logic
                toast.dismiss(loadingToastId);
            }
        }
    };

    // console.log("FormData")
    // console.log(formData)

    return (
        <>
            <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
            <button className="model-edit-button" title="Accounts Final Decision" onClick={handleShow}>
                <CheckGreen />
            </button>
            <Modal show={show} onHide={handleClose} dialogClassName="request-leave width-40vw">
                <Modal.Body>
                    <div className="parent-div">

                        <div className="bdy-div">
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">Confirm Stage {i.level} {i.employee ? (i.role ? `${i.employee?.name}(${i.role?.role_name})` : `${i.employee?.name}`) : i.role?.role_name} Approval of Expense : "{j.name}" </h6>

                            <div className="flex-row">
                                <label htmlFor="date" className="form-labels font-weight500 announce-date font-weight400  font-size-heading">
                                    Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(j.date)}
                                </div>


                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Expense against Imprest :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {j.imprest_data?.imprest_name}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    For Project-
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {j.imprest_data?.project_short_name}
                                </div>
                            </div>

                            {/* <hr className="line" /> */}
                            <hr className="line" />
                            <div className="children">
                                <div>
                                    <label htmlFor="fooding" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                        Utilized Amount
                                    </label>
                                    <p className="utilize   font-weight500   -amount align-center" style={{ color: "red" }}>
                                        {j.utilized_amount}
                                    </p>
                                </div>

                                <div>
                                    <label htmlFor="fooding" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                        Remaining Amount
                                    </label>
                                    <p className="remaining-amount  font-weight500    align-center" style={{ color: "#2576bc" }}>
                                        {j.remaining_amount}
                                    </p>
                                </div>
                            </div>
                            <hr className="line" />

                            <div className="flex-column form-group-selectt">
                                <label htmlFor="decision" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Final Decision
                                </label>
                                <br />
                                <select
                                    id="decision"
                                    name="decision"
                                    value={formData.decision}
                                    onChange={(e) => {
                                        handleInputChange(e);
                                        handleClear();
                                    }}
                                    className={`form-input form-group-selection ${errors.decision ? "error" : formData.decision ? "success" : ""}`}
                                >
                                    <option value="">Select Decision</option>
                                    <option value="Paid">Paid</option>
                                    <option value="Salary">To Salary</option>
                                    <option value="Balance">To Balance</option>
                                </select>

                            </div>
                            <br />
                            {errors.decision && (
                                <span className="error-message font-size-text ">{errors.decision}</span>
                            )}


                            {formData.decision === 'Paid' && (
                                <>
                                    <div className="flex-column">
                                        <label
                                            htmlFor="settlement_amount"
                                            className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                        >
                                            Settlement Amount
                                        </label>
                                        <input
                                            id="settlement_amount"
                                            name="settlement_amount"
                                            type="number"
                                            min={0}
                                            placeholder="Settlement Amount"
                                            onWheel={(e) => e.target.blur()}
                                            onChange={handleInputChange}
                                            value={formData.settlement_amount}
                                            className={`form-input ${errors.settlement_amount
                                                ? "error"
                                                : inputState.settlement_amount
                                                    ? "success"
                                                    : ""
                                                }`}
                                        />
                                        {errors.settlement_amount && (
                                            <span className="error-message font-size-text ">{errors.settlement_amount}</span>
                                        )}
                                    </div>

                                    <div className="flex-row">
                                        <label htmlFor="decision" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                            Settlement Bill :
                                        </label>
                                        <div className="flex-column" >
                                            <label
                                                htmlFor="settlement_bill"
                                                className={`align-center  form-inputss custom-file-upload 
                      ${errors.settlement_bill
                                                        ? "error"
                                                        : inputState.settlement_bill
                                                            ? "success"
                                                            : ""
                                                    }
                         `}
                                            >
                                                <div className={`align-center pdf-input  ${errors.settlement_bill ? 'error' : ''}`}>
                                                    <Attachment />
                                                </div>
                                            </label>
                                            <input
                                                id="settlement_bill"
                                                name="settlement_bill"
                                                type="file"
                                                onChange={handleFileChange}
                                                onFocus={handleClear}
                                                className="file-inputs align-center"
                                                style={{ display: "none", position: "relative" }}
                                            />
                                            <br />
                                            <div className="flex-row justify-evenly">
                                                <div>
                                                    {fileName && <p style={{ margin: "0px" }}>{fileName}</p>}
                                                </div>
                                            </div>
                                        </div>
                                        {formData.settlement_bill ?
                                            <div>
                                                <button className="model-button" onClick={handleClear}>Clear</button>
                                            </div>
                                            : null
                                        }
                                        {errors.settlement_bill && (
                                            <span className="error-message font-size-text ">{errors.settlement_bill}</span>
                                        )}
                                    </div>

                                </>
                            )}

                            {formData.decision === 'Salary' && (
                                <>
                                    <div className="flex-column">
                                        <label
                                            htmlFor="settlement_amount"
                                            className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                        >
                                            Settlement Amount
                                        </label>
                                        <input
                                            id="settlement_amount"
                                            name="settlement_amount"
                                            type="number"
                                            min={0}
                                            placeholder="Settlement Amount"
                                            onWheel={(e) => e.target.blur()}
                                            onChange={handleInputChange}
                                            value={formData.settlement_amount}
                                            className={`form-input ${errors.settlement_amount
                                                ? "error"
                                                : inputState.settlement_amount
                                                    ? "success"
                                                    : ""
                                                }`}
                                        />
                                        {errors.settlement_amount && (
                                            <span className="error-message font-size-text ">{errors.settlement_amount}</span>
                                        )}
                                    </div>
                                </>
                            )}
                            <br />
                            <div className="button-models">
                                <button className="model-button model-button-cancel  font-weight500" onClick={handleClose}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="model-button font-weight500 model-button-submit  font-weight500    font-size-heading"
                                    onClick={handleAccountFinalize}
                                    disabled={loading}
                                >
                                    {loading ?
                                        <div className='flex-row'>
                                            Submitting...
                                            <div className="spinner-vsmall"></div>
                                        </div>
                                        : "Submit"}
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal >
        </>
    );
};

const DynamicRequestExpenseRejection = ({ i, j, k, getExpenseData, handleBackRoute }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const [formData, setFormData] = useState({ remarks: "" });
    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});

    const handleClose = () => setShow(false);
    const handleShow = () => {
        setFormData({ remarks: "" });
        setErrors({});
        setInputState({});
        setShow(true);
    };

    const sum = j?.some(expense => expense.bill_approved_amt === null)
        ? j?.reduce((acc, item) => acc + parseFloat(item.item_amount || 0), 0)
        : j?.reduce((acc, item) => acc + parseFloat(item.bill_approved_amt || 0), 0);

    const total =
        Number(i.imprest_data?.approved_stationary_item || 0) +
        Number(i.imprest_data?.approved_electronic_item || 0) +
        Number(i.imprest_data?.approved_project_imprest_monthly || 0) +
        Number(i.imprest_data?.approved_grocery || 0) +
        Number(i.imprest_data?.other_details?.length > 0
            ? i.imprest_data?.other_details?.reduce((total, other) => total + Number(other.approved_other || 0), 0)
            : 0);

    const remaining = total - sum;

    const approverLabel = k.employee ? (k.role ? `${k.employee?.name} (${k.role?.role_name})` : k.employee?.name) : k.role?.role_name;

    //******************Validation Start********************* */
    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ["remarks"];
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setInputState({
            ...inputState,
            [name]: value.trim() ? "success" : "",
        });
        setFormData({
            ...formData,
            [name]: value,
        });
        setErrors({
            ...errors,
            [name]: "",
        });
    };
    //******************Validation End********************* */

    const handleDynamicRejection = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(0);

        const loadingToastId = toast.loading("Loading: Please wait...");

        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            if (progress >= 100) {
                progress = 100;
                clearInterval(interval);
            }
            setUploadProgress(progress);
        }, 100);

        let RejectedStatus = {
            approval_status: 'rejected',
            remarks: formData.remarks,
            updated_at: new Date().toISOString(),
            updated_by: sessionStorage.getItem('sitemanagement_emp_id')
        };

        try {
            let req = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${k.id}/`, RejectedStatus);

            if (req.status === 200 || req.status === 201) {
                clearInterval(interval);
                toast.dismiss(loadingToastId);
                toast.success("Expense Request Rejected Successfully");
                handleClose();
                // await getExpenseData();
                toast.info("Navigating to Expense Requests Page");
                handleBackRoute();
            } else {
                clearInterval(interval);
                setUploadProgress(0);
                toast.error(`Expense Request Update Error: ${req.statusText}`);
            }
        } catch (err) {
            clearInterval(interval);
            setUploadProgress(0);
            handleErrorToast(err, loadingToastId);
        } finally {
            clearInterval(interval);
            setTimeout(() => {
                setShowProgressModal(false);
            }, 1500);
            setLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    return (
        <>
            <style>{`
                .dea-info-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 12px 20px;
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 14px 18px;
                    margin-bottom: 18px;
                }
                .dea-info-item {
                    display: flex;
                    flex-direction: column;
                    gap: 3px;
                }
                .dea-info-label {
                    font-size: 11px;
                    font-weight: 600;
                    color: #94a3b8;
                    text-transform: uppercase;
                    letter-spacing: 0.4px;
                }
                .dea-info-value {
                    font-size: 13.5px;
                    color: #1f2937;
                    font-weight: 600;
                }
                .dea-amount-card {
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 14px 18px;
                }
                .dea-amount-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 7px 0;
                    font-size: 13.5px;
                    color: #475569;
                }
                .dea-amount-row:not(:last-child) {
                    border-bottom: 1px dashed #e2e8f0;
                }
                .dea-amount-row.total {
                    font-weight: 700;
                    color: #1f2937;
                    font-size: 15px;
                    padding-top: 10px;
                }
            `}</style>

            <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

            <button className="btn-primaryy" style={{ backgroundColor: "#00000075" }} title={`Expense Stage ${k.level} Rejection`} onClick={handleShow}>
                Reject
            </button>

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Reject" />
            ) : (
                <Modal show={show} onHide={handleClose} dialogClassName="modern-project-modal" size="lg">
                    <Modal.Header className="modern-modal-header">
                        <div className="header-content">
                            <div style={{ color: "white" }}>
                                <CrossRed size={24} />
                            </div>
                            <div>
                                <Modal.Title>Reject Stage {k.level} Approval</Modal.Title>
                                <p className="header-subtitle" style={{ color: "white" }}>
                                    {approverLabel} • Expense "{i.name}"
                                </p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={handleClose}>
                            <X size={20} />
                        </button>
                    </Modal.Header>

                    <Modal.Body className="modern-modal-body">
                        <div className="dea-info-grid">
                            <div className="dea-info-item">
                                <span className="dea-info-label">Date</span>
                                <span className="dea-info-value">{formattedDateLong(i.date)}</span>
                            </div>
                            <div className="dea-info-item">
                                <span className="dea-info-label">Expense against Imprest</span>
                                <span className="dea-info-value">{i.project_name}</span>
                            </div>
                        </div>

                        <div className="section-title">
                            <Wallet size={18} />
                            <h4>Amount Breakdown</h4>
                        </div>

                        <div className="dea-amount-card">
                            <div className="dea-amount-row">
                                <span>Utilized Amount</span>
                                <span style={{ color: "#dc2626", fontWeight: 700 }}>{formatCurrencyIndian(sum)}</span>
                            </div>
                            <div className="dea-amount-row total">
                                <span>Remaining Amount</span>
                                <span style={{ color: "#2576bc" }}>{formatCurrencyIndian(remaining)}</span>
                            </div>
                        </div>

                        <div className="form-field full-width" style={{ marginTop: '18px' }}>
                            <label htmlFor="remarks">Rejection Reason<span className="required">*</span></label>
                            <textarea
                                id="remarks"
                                name="remarks"
                                placeholder="Your Reason"
                                onChange={handleInputChange}
                                maxLength={100}
                                value={formData.remarks}
                                rows={3}
                                className={errors.remarks ? "error" : inputState.remarks ? "success" : ""}
                            />
                            {errors.remarks && (
                                <span className="error-message">{errors.remarks}</span>
                            )}
                        </div>

                        <div className="form-actions">
                            <button type="button" className="btn-primaryy" style={{ background: "#00000075" }} onClick={handleClose}>
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn-primaryy btn-danger"
                                onClick={handleDynamicRejection}
                                disabled={loading}
                            >
                                {loading ? <Loader size={18} className="spinning" /> : <X size={18} />}
                                <span>{loading ? 'Rejecting...' : 'Reject'}</span>
                            </button>
                        </div>
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};

const DynamicRequestOfficeApproval = ({ i, getDynamicRequestData }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const approverLabel = i.employee ? (i.role ? `${i.employee?.name} (${i.role?.role_name})` : i.employee?.name) : i.role?.role_name;

    const handleFormSubmitPut = async (e) => {
        e.preventDefault();
        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(0);

        const loadingToastId = toast.loading("Loading: Please wait...");

        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            if (progress >= 100) {
                progress = 100;
                clearInterval(interval);
            }
            setUploadProgress(progress);
        }, 100);

        let ApprovalFormData = {
            approval_status: 'approved',
            updated_by: `${sessionStorage.getItem('sitemanagement_emp_id')}`,
            updated_at: new Date().toISOString(),
        };

        try {
            let req = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${i.id}/`, ApprovalFormData);

            if (req.status === 200 || req.status === 201) {
                clearInterval(interval);
                toast.dismiss(loadingToastId);
                toast.success("Office Request Status Updated Successfully");
                await getDynamicRequestData();
                handleClose();
            } else {
                clearInterval(interval);
                setUploadProgress(0);
                toast.error(`Office Request Update Error: ${req.statusText}`);
            }
        } catch (err) {
            clearInterval(interval);
            setUploadProgress(0);
            handleErrorToast(err, loadingToastId);
        } finally {
            clearInterval(interval);
            setTimeout(() => {
                setShowProgressModal(false);
            }, 1500);
            setLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    return (
        <>
            <style>{`
                .droa-info-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 12px 20px;
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 14px 18px;
                    margin-bottom: 18px;
                }
                .droa-info-item {
                    display: flex;
                    flex-direction: column;
                    gap: 3px;
                }
                .droa-info-label {
                    font-size: 11px;
                    font-weight: 600;
                    color: #94a3b8;
                    text-transform: uppercase;
                    letter-spacing: 0.4px;
                }
                .droa-info-value {
                    font-size: 13.5px;
                    color: #1f2937;
                    font-weight: 600;
                }
                .droa-amount-card {
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 14px 18px;
                }
                .droa-amount-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 7px 0;
                    font-size: 13.5px;
                    color: #475569;
                }
                .droa-amount-row:not(:last-child) {
                    border-bottom: 1px dashed #e2e8f0;
                }
                .droa-amount-row.total {
                    font-weight: 700;
                    color: #1f2937;
                    font-size: 15px;
                    padding-top: 10px;
                }
            `}</style>

            <button className="model-edit-button-modern actionn-icon" style={{ background: "#00000075" }} title={`Office Stage ${i.level} Approval`} onClick={handleShow}>
                <Check size={20} color='white' />
            </button>

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Add" />
            ) : (
                <Modal show={show} onHide={handleClose} dialogClassName="modern-project-modal" size="lg">
                    <Modal.Header className="modern-modal-header">
                        <div className="header-content">
                            <div style={{ color: "white" }}>
                                <CheckCircle size={20} />
                            </div>
                            <div>
                                <Modal.Title>Confirm Stage {i.level} Approval</Modal.Title>
                                <p className="header-subtitle" style={{ color: "white" }}>
                                    {approverLabel} • Office Rent "{i?.officerent_billing_details?.project_short_name}"
                                </p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={handleClose}>
                            <X size={20} />
                        </button>
                    </Modal.Header>

                    <Modal.Body className="modern-modal-body">
                        <div className="droa-info-grid">
                            <div className="droa-info-item">
                                <span className="droa-info-label">Bill Generate Date</span>
                                <span className="droa-info-value">{formattedDateLong(i?.officerent_billing_details?.date)}</span>
                            </div>
                            <div className="droa-info-item">
                                <span className="droa-info-label">Rent Period</span>
                                <span className="droa-info-value">{getMonthName(i?.officerent_billing_details?.month)}/{i?.officerent_billing_details?.year}</span>
                            </div>
                            <div className="droa-info-item">
                                <span className="droa-info-label">Owner Name</span>
                                <span className="droa-info-value">{i?.officerent_billing_details?.owner_name}</span>
                            </div>
                            <div className="droa-info-item">
                                <span className="droa-info-label">Account Holder Name</span>
                                <span className="droa-info-value">{i?.officerent_billing_details?.account_holder_name}</span>
                            </div>
                        </div>

                        <div className="section-title">
                            <Wallet size={18} />
                            <h4>Amount Breakdown</h4>
                        </div>

                        <div className="droa-amount-card">
                            <div className="droa-amount-row">
                                <span>Rent Amount</span>
                                <span>{formatCurrencyIndian(i?.officerent_billing_details?.rent_amt)}</span>
                            </div>
                            <div className="droa-amount-row">
                                <span>TDS ({i?.officerent_billing_details?.tds_percentage}%)</span>
                                <span>{formatCurrencyIndian(i?.officerent_billing_details?.tds_amt || 0)}</span>
                            </div>
                            <div className="droa-amount-row total">
                                <span>Total Amount</span>
                                <span>{formatCurrencyIndian(i?.officerent_billing_details?.total_amt)}</span>
                            </div>
                        </div>

                        <div className="form-actions">
                            <button type="button" className="btn-secondaryy" onClick={handleClose}>
                                Cancel
                            </button>
                            <button type="submit" className="btn-primaryy" onClick={handleFormSubmitPut} disabled={loading}>
                                {loading ? <Loader size={18} className="spinning" /> : <Check size={18} />}
                                <span>{loading ? 'Confirming...' : 'Confirm'}</span>
                            </button>
                        </div>

                        <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};
const DynamicBillingFinal = ({ i, getofficebillingdata, documentlist }) => {

    console.log(i, "i final")
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);


    const [fileName, setFileName] = useState("");
    const [formData, setFormData] = useState({
    });


    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});
    const validateForm = () => {
        const newErrors = {};
        const requiredFields = [
            "settlement_transaction_id",
            "settlement_bill"
        ];
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setInputState({
            ...inputState,
            [name]: value.trim() ? "green" : "",
        });

        setFormData({
            ...formData,
            [name]: value,
        });
    };
    const handleFileChange = (e) => {
        const { name } = e.target;
        const file = e.target.files[0];

        if (file) {
            setFormData({ ...formData, [name]: file, });
            setFileName(file.name);
        } else {
            setFileName("");
        }
    };

    const handleClear = () => {
        setFormData(prevState => ({
            ...prevState,
            settlement_bill: null
        }));
        setFileName("");

    }

    const handleFormSubmitPut = async (e) => {

        e.preventDefault();

        const formDataa = new FormData();

        const symbols = '!@#$%^&*()_-+=';
        const lowercaseLetters = 'abcdefghijklmnopqrstuvwxyz';
        const uppercaseLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const numbers = '0123456789';
        const now = new Date();
        const date = now.toLocaleDateString('en-GB').split('/').reverse().join('-'); // Format: YYYY-MM-DD
        const time = now.toLocaleTimeString('en-GB', { hour12: false }).replace(/:/g, '-'); // Format: HH-MM-SS
        const allChars = symbols + lowercaseLetters + uppercaseLetters + numbers;
        let randomCode = '';
        for (let i = 0; i < 8; i++) {
            const randomIndex = Math.floor(Math.random() * allChars.length);
            randomCode += allChars[randomIndex];
        }
        if (formData.settlement_bill && formData.settlement_transaction_id) {
            const originalFile = formData?.settlement_bill;
            const customFileName = `Aimantra ${date} ${time}_${randomCode} ${originalFile?.name}`;
            const customFile = new File([originalFile], customFileName, { type: originalFile.type });

            formDataa.append("settlement_transaction_id", formData.settlement_transaction_id);
            formDataa.append("settlement_bill", customFile);
        }

        if (validateForm()) {
            const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic
            let ApprovalFormData = {
                approval_status: 'approved',
                updated_at: new Date().toISOString(),
                updated_by: sessionStorage.getItem('sitemanagement_emp_id')
            };
            try {
                let res = await axios.put(`${BASE_URL}/siteproject/officerentbillingupdate/${i?.officerent_billing_details?.id}/`, formDataa, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                });

                if (res.status === 200 || res.status === 201) {
                    toast.success("Office Billing Updated Successfully");

                    let req = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${i.id}/`, ApprovalFormData,);
                    if (req.status === 200 || req.status === 201) {
                        toast.success("Office Request Status Updated Successfully");
                        await getofficebillingdata();
                        setShow(false);
                    } else {
                        toast.error(`Office  Request Update Error: ${req.statusText}`);
                        console.log('Office  Request Update Error:')
                        console.error(req);

                    }


                } else {
                    toast.error(`Office Update Error: ${res.statusText}`);
                    console.log('Office Update Error:')
                    console.error(res);
                }

            } catch (err) {
                handleErrorToast(err, loadingToastId)
            } finally {
                toast.dismiss(loadingToastId);
            }
        }
    };

    return (
        <>
            <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
            {(documentlist != undefined && documentlist.length === 0) || documentlist === undefined ? (<> <span className="error-message font-size-text " style={{ marginTop: '0px', marginBottom: '0px', paddingRight: '5px' }}>No Documents Attached</span></>) : (<>
                <button className="model-edit-button" title="Account Stage 2 Approval" onClick={handleShow}>
                    <CheckGreen />
                </button>
            </>)}

            <Modal show={show} onHide={handleClose} dialogClassName="request-leave width-40vw">
                <Modal.Body>

                    <div className="parent-div">

                        <div className="bdy-div">
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">
                                Account Finalize for "{i.project_short_name}"
                            </h6>
                            <div className="flex-row">
                                <label htmlFor="date" className="form-labels font-weight500 announce-date font-weight400  font-size-heading">
                                    Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {formattedDateLong(i?.officerent_billing_details?.date)}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Project :
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.officerent_billing_details?.project_short_name}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Owner Name:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.officerent_billing_details?.owner_name}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Days:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.officerent_billing_details?.days}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Months:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {getMonthName(i?.officerent_billing_details?.month)}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Year:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {i?.officerent_billing_details?.year}
                                </div>
                            </div>


                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Basic Amount:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {formatCurrencyIndian(i?.officerent_billing_details?.rent_amt)}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    TDS Percentage:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {formatCurrencyIndian(i?.officerent_billing_details?.tds_percentage)}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    TDS Amount:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {formatCurrencyIndian(i?.officerent_billing_details?.tds_amt)}
                                </div>
                            </div>
                            <div className="flex-row">
                                <label
                                    htmlFor="project_name"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Total Amount:
                                </label>
                                <div className="form-labels announce-date font-size-heading padding-lefttt">
                                    {formatCurrencyIndian(i?.officerent_billing_details?.total_amt)}
                                </div>
                            </div>
                            <div className="flex-row">
                                <ViewDocumnetsbilling documentlist={documentlist} />
                            </div>
                            <>
                                <div className="flex-column">
                                    <label
                                        htmlFor="settlement_transaction_id"
                                        className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                    >
                                        Final Approval Details (Transaction / Cheque no.)
                                    </label>
                                    <input
                                        id="settlement_transaction_id"
                                        name="settlement_transaction_id"
                                        placeholder="Final Approval Details (Transaction / Cheque no.)"
                                        onChange={handleInputChange}
                                        value={formData.settlement_transaction_id}
                                        className={`form-input ${errors.settlement_transaction_id
                                            ? "error"
                                            : inputState.settlement_transaction_id
                                                ? "success"
                                                : ""
                                            }`}
                                    />
                                    {errors.settlement_transaction_id && (
                                        <span className="error-message font-size-text ">{errors.settlement_transaction_id}</span>
                                    )}
                                </div>

                                <div className="flex-row">
                                    <label htmlFor="" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                        Account Transaction Proof :
                                    </label>
                                    <div className="flex-column" >
                                        <label
                                            htmlFor="settlement_bill"
                                            className={`align-center  form-inputss custom-file-upload 
                                                    ${errors.settlement_bill
                                                    ? "error"
                                                    : inputState.settlement_bill
                                                        ? "success"
                                                        : ""
                                                }`}
                                        >
                                            <div className="svg-field form-input align-center">
                                                <Attachment />
                                            </div>
                                        </label>
                                        <input
                                            id="settlement_bill"
                                            name="settlement_bill"
                                            type="file"
                                            onChange={handleFileChange}
                                            onFocus={handleClear}
                                            className="file-inputs align-center"
                                            style={{ display: "none", position: "relative" }}
                                        />
                                        <br />
                                        <div className="flex-row justify-evenly">
                                            <div>
                                                {fileName && <p style={{ margin: "0px" }}>{fileName}</p>}
                                            </div>
                                        </div>
                                    </div>
                                    {formData.settlement_bill ?
                                        <div>
                                            <button className="model-button" onClick={handleClear}>Clear</button>
                                        </div>
                                        : null
                                    }
                                    {errors.settlement_bill && (
                                        <span className="error-message font-size-text ">{errors.settlement_bill}</span>
                                    )}
                                </div>
                            </>

                            <div className="button-models">
                                <button className="model-button model-button-cancel font-weight500" onClick={handleClose}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="model-button font-weight500 model-button-leave font-size-heading"
                                    onClick={handleFormSubmitPut}
                                >
                                    Approve
                                </button>
                            </div>
                        </div>


                    </div>

                </Modal.Body>
            </Modal >
        </>
    );
};


const DynamicRequestOfficeRejection = ({ i, getDynamicRequestData }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => {
        setFormData({ remarks: "" });
        setErrors({});
        setInputState({});
        setShow(true);
    };

    const [formData, setFormData] = useState({
        remarks: "",
    });

    //******************Validation Start********************* */

    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ["remarks"];
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setInputState({
            ...inputState,
            [name]: value.trim() ? "success" : "",
        });

        setFormData({
            ...formData,
            [name]: value,
        });

        setErrors({
            ...errors,
            [name]: "",
        });
    };

    const approverLabel = i.employee ? (i.role ? `${i.employee?.name} (${i.role?.role_name})` : i.employee?.name) : i.role?.role_name;

    const handleFormSubmitPut = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(0);

        const loadingToastId = toast.loading("Loading: Please wait...");

        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            if (progress >= 100) {
                progress = 100;
                clearInterval(interval);
            }
            setUploadProgress(progress);
        }, 100);

        let updatedFormData = {
            approval_status: 'rejected',
            remarks: formData.remarks,
            updated_by: sessionStorage.getItem('sitemanagement_emp_id'),
            updated_at: new Date().toISOString(),
        };

        try {
            let res = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${i.id}/`, updatedFormData);

            if (res.status === 200 || res.status === 201) {
                clearInterval(interval);
                toast.dismiss(loadingToastId);
                toast.success("Office Request Status Updated Successfully");
                await getDynamicRequestData();
                handleClose();
            } else {
                clearInterval(interval);
                setUploadProgress(0);
                toast.error(`Office Request Update Error: ${res.statusText}`);
                console.error(res);
            }
        } catch (err) {
            clearInterval(interval);
            setUploadProgress(0);
            handleErrorToast(err, loadingToastId);
        } finally {
            clearInterval(interval);
            setTimeout(() => {
                setShowProgressModal(false);
            }, 1500);
            setLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    return (
        <>
            <style>{`
                .droa-info-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 12px 20px;
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 14px 18px;
                    margin-bottom: 18px;
                }
                .droa-info-item {
                    display: flex;
                    flex-direction: column;
                    gap: 3px;
                }
                .droa-info-label {
                    font-size: 11px;
                    font-weight: 600;
                    color: #94a3b8;
                    text-transform: uppercase;
                    letter-spacing: 0.4px;
                }
                .droa-info-value {
                    font-size: 13.5px;
                    color: #1f2937;
                    font-weight: 600;
                }
                .droa-amount-card {
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 14px 18px;
                }
                .droa-amount-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 7px 0;
                    font-size: 13.5px;
                    color: #475569;
                }
                .droa-amount-row:not(:last-child) {
                    border-bottom: 1px dashed #e2e8f0;
                }
                .droa-amount-row.total {
                    font-weight: 700;
                    color: #1f2937;
                    font-size: 15px;
                    padding-top: 10px;
                }
            `}</style>

            <button className="model-delete-button-modern actionn-icon" style={{ backgroundColor: "#00000075" }} title={`Office Stage ${i.level} Rejection`} onClick={handleShow}>
                <X size={20} color='white' />
            </button>

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Reject" />
            ) : (
                <Modal show={show} onHide={handleClose} dialogClassName="modern-project-modal" size="lg">
                    <Modal.Header className="modern-modal-header">
                        <div className="header-content">
                            <div style={{ color: "white" }}>
                                <CrossRed size={24} />
                            </div>
                            <div>
                                <Modal.Title>Reject Stage {i.level} Approval</Modal.Title>
                                <p className="header-subtitle" style={{ color: "white" }}>
                                    {approverLabel} • Office Rent "{i?.officerent_billing_details?.project_short_name}"
                                </p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={handleClose}>
                            <X size={20} />
                        </button>
                    </Modal.Header>

                    <Modal.Body className="modern-modal-body">
                        <div className="droa-info-grid">
                            <div className="droa-info-item">
                                <span className="droa-info-label">Bill Generate Date</span>
                                <span className="droa-info-value">{formattedDateLong(i?.officerent_billing_details?.date)}</span>
                            </div>
                            <div className="droa-info-item">
                                <span className="droa-info-label">Rent Period</span>
                                <span className="droa-info-value">{getMonthName(i?.officerent_billing_details?.month)}/{i?.officerent_billing_details?.year}</span>
                            </div>
                            <div className="droa-info-item">
                                <span className="droa-info-label">Owner Name</span>
                                <span className="droa-info-value">{i?.officerent_billing_details?.owner_name}</span>
                            </div>
                            <div className="droa-info-item">
                                <span className="droa-info-label">Account Holder Name</span>
                                <span className="droa-info-value">{i?.officerent_billing_details?.account_holder_name}</span>
                            </div>
                        </div>

                        <div className="section-title">
                            <Wallet size={18} />
                            <h4>Amount Breakdown</h4>
                        </div>

                        <div className="droa-amount-card">
                            <div className="droa-amount-row">
                                <span>Rent Amount</span>
                                <span>{formatCurrencyIndian(i?.officerent_billing_details?.rent_amt)}</span>
                            </div>
                            <div className="droa-amount-row">
                                <span>TDS ({i?.officerent_billing_details?.tds_percentage}%)</span>
                                <span>{formatCurrencyIndian(i?.officerent_billing_details?.tds_amt || 0)}</span>
                            </div>
                            <div className="droa-amount-row total">
                                <span>Total Amount</span>
                                <span>{formatCurrencyIndian(i?.officerent_billing_details?.total_amt)}</span>
                            </div>
                        </div>

                        <div className="form-field full-width" style={{ marginTop: '18px' }}>
                            <label htmlFor="remarks">Rejection Reason<span className="required">*</span></label>
                            <textarea
                                id="remarks"
                                name="remarks"
                                placeholder="Your Reason"
                                onChange={handleInputChange}
                                maxLength={100}
                                value={formData.remarks}
                                rows={3}
                                className={errors.remarks ? "error" : inputState.remarks ? "success" : ""}
                            />
                            {errors.remarks && (
                                <span className="error-message">{errors.remarks}</span>
                            )}
                        </div>

                        <div className="form-actions">
                            <button type="button" className="btn-secondaryy" onClick={handleClose}>
                                Cancel
                            </button>
                            <button type="submit" className="btn-primaryy btn-danger" onClick={handleFormSubmitPut} disabled={loading}>
                                {loading ? <Loader size={18} className="spinning" /> : <X size={18} />}
                                <span>{loading ? 'Submitting...' : 'Reject'}</span>
                            </button>
                        </div>

                        <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};

const DynamicRequestOfficeEditApproval = ({ i, getDynamicRequestData, documentlist }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const [maxlimit, setMaxlimit] = useState('')
    const [maxlimittds, setMaxlimittds] = useState('');

    const handleClose = () => setShow(false);
    const handleShow = () => {
        setMaxlimittds(parseFloat(i?.officerent_billing_details?.tds_percentage).toFixed(2))
        setMaxlimit(parseFloat(i?.officerent_billing_details?.rent_amt).toFixed(2));
        setFormData(i?.officerent_billing_details)
        setErrors({});
        setInputState({});
        setShow(true)
    };

    const [formData, setFormData] = useState({
        rent_amt: '',
        tds_percentage: '',
        tds_amt: '',
        total_amt: '',
    });

    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});

    const approverLabel = i.employee ? (i.role ? `${i.employee?.name} (${i.role?.role_name})` : i.employee?.name) : i.role?.role_name;

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ['rent_amt', 'total_amt']
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let updatedData = { ...formData, [name]: value };

        if (name === "rent_amt") {
            const rentAmount = parseFloat(value).toFixed(2);
            const { success, error } = inputMaxLimit(name, rentAmount, maxlimit);
            if (!success) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    [name]: error,
                }));
                return updatedData;
            } else {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    [name]: "",
                }));
                if (rentAmount >= 50000) {
                    const tdsPercentage = 10;
                    const tdsAmount = parseFloat(((rentAmount * tdsPercentage) / 100).toFixed(2));
                    updatedData = {
                        ...updatedData,
                        tds_percentage: tdsPercentage,
                        tds_amt: tdsAmount,
                        total_amt: parseFloat((parseFloat(rentAmount) - tdsAmount).toFixed(2)),
                    };
                } else {
                    updatedData = {
                        ...updatedData,
                        tds_percentage: "",
                        tds_amt: 0,
                        total_amt: parseFloat(rentAmount),
                    };
                }
            }
        }

        if (name === "tds_percentage" && Number(formData.rent_amt) >= 50000) {
            const tdsPercentage = parseFloat(value).toFixed(2);
            const { success, error } = inputMaxLimit(name, tdsPercentage, maxlimittds);
            if (!success) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    [name]: error,
                }));
                return updatedData;
            } else {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    [name]: "",
                }));
                const tdsAmount = parseFloat(((Number(formData.rent_amt) * tdsPercentage) / 100).toFixed(2));
                updatedData = {
                    ...updatedData,
                    tds_amt: tdsAmount,
                    total_amt: parseFloat((Number(formData.rent_amt) - tdsAmount).toFixed(2)),
                };
            }
        }

        setFormData(updatedData);

        setInputState({
            ...inputState,
            [name]: value.trim() ? "success" : "",
        });

        if (errors[name]) {
            setErrors((prevErrors) => {
                const { [name]: removedError, ...restErrors } = prevErrors;
                return restErrors;
            });
        }
    };

    const handleFormSubmitPut = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        let ApprovalFormData = {
            ...formData,
            approval_status: 'approved',
            updated_by: `${sessionStorage.getItem('sitemanagement_emp_id')}`,
            updated_at: new Date().toISOString(),
        };

        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(0);

        const loadingToastId = toast.loading("Loading: Please wait...");

        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            if (progress >= 100) {
                progress = 100;
                clearInterval(interval);
            }
            setUploadProgress(progress);
        }, 100);

        try {
            let off = await axios.put(`${BASE_URL}/siteproject/officerentbillingupdate/${i.officerent_billing_details?.id}/`, formData);

            if (off.status === 200 || off.status === 201) {
                let res = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${i.id}/`, ApprovalFormData);

                if (res.status === 200 || res.status === 201) {
                    clearInterval(interval);
                    toast.dismiss(loadingToastId);
                    toast.success("Office Request Partially Approved Successfully");
                    await getDynamicRequestData();
                    handleClose();
                } else {
                    clearInterval(interval);
                    setUploadProgress(0);
                    toast.error(`Office Request Update Error: ${res.statusText}`);
                }
            } else {
                clearInterval(interval);
                setUploadProgress(0);
                toast.error(`Office Rent Update Error: ${off.statusText}`);
            }
        } catch (err) {
            clearInterval(interval);
            setUploadProgress(0);
            handleErrorToast(err, loadingToastId);
        } finally {
            clearInterval(interval);
            setTimeout(() => {
                setShowProgressModal(false);
            }, 1500);
            setLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    return (
        <>
            <style>{`
                .droea-info-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 12px 20px;
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 14px 18px;
                    margin-bottom: 18px;
                }
                .droea-info-item {
                    display: flex;
                    flex-direction: column;
                    gap: 3px;
                }
                .droea-info-label {
                    font-size: 11px;
                    font-weight: 600;
                    color: #94a3b8;
                    text-transform: uppercase;
                    letter-spacing: 0.4px;
                }
                .droea-info-value {
                    font-size: 13.5px;
                    color: #1f2937;
                    font-weight: 600;
                }
                .droea-fields-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 16px 20px;
                    margin-bottom: 8px;
                }
            `}</style>

            <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

            {(documentlist != undefined && documentlist.length === 0) || documentlist === undefined ? (
                <span className="error-message font-size-text" style={{ marginTop: '0px', marginBottom: '0px', paddingRight: '5px' }}>No Documents Attached</span>
            ) : (
                <button className="model-delete-button-modern actionn-icon" style={{ backgroundColor: "#00000075" }} title={`Office Stage ${i.level} Editable Approval`} onClick={handleShow}>
                    <WarnYellow />
                </button>
            )}

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Partial Approve" />
            ) : (
                <Modal show={show} onHide={handleClose} dialogClassName="modern-project-modal" size="lg">
                    <Modal.Header className="modern-modal-header">
                        <div className="header-content">
                            <div style={{ color: "white" }}>
                                <WarnYellow size={20} />
                            </div>
                            <div>
                                <Modal.Title>Partial Approve Stage {i.level} Approval</Modal.Title>
                                <p className="header-subtitle" style={{ color: "white" }}>
                                    {approverLabel} • Office Rent "{i?.officerent_billing_details?.project_short_name}"
                                </p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={handleClose}>
                            <X size={20} />
                        </button>
                    </Modal.Header>

                    <Modal.Body className="modern-modal-body">
                        <div className="droea-info-grid">
                            <div className="droea-info-item">
                                <span className="droea-info-label">Bill Generate Date</span>
                                <span className="droea-info-value">{formattedDateLong(i?.officerent_billing_details?.date)}</span>
                            </div>
                            <div className="droea-info-item">
                                <span className="droea-info-label">Rent Period</span>
                                <span className="droea-info-value">{getMonthName(i?.officerent_billing_details?.month)}/{i?.officerent_billing_details?.year}</span>
                            </div>
                            <div className="droea-info-item">
                                <span className="droea-info-label">Owner Name</span>
                                <span className="droea-info-value">{i?.officerent_billing_details?.owner_name}</span>
                            </div>
                            <div className="droea-info-item">
                                <span className="droea-info-label">Account Holder Name</span>
                                <span className="droea-info-value">{i?.officerent_billing_details?.account_holder_name}</span>
                            </div>
                        </div>

                        <div className="section-title">
                            <Wallet size={18} />
                            <h4>Editable Amount</h4>
                        </div>

                        <div className="droea-fields-grid">
                            <div className="form-field">
                                <label htmlFor="rent_amt">Basic Amount<span className="required">*</span></label>
                                <input
                                    type="number"
                                    min={0}
                                    max={i.rent_amt}
                                    maxLength={10}
                                    id="rent_amt"
                                    name="rent_amt"
                                    placeholder="Basic Amount"
                                    onChange={handleInputChange}
                                    value={formData.rent_amt}
                                    className={errors.rent_amt ? "error" : inputState.rent_amt ? "success" : ""}
                                />
                                {errors.rent_amt && (
                                    <span className="error-message">{errors.rent_amt}</span>
                                )}
                            </div>

                            {Number(formData.rent_amt) <= 50000 ? (
                                <div className="form-field">
                                    <label htmlFor="total_amt">Total Amount<span className="required">(Auto Calculated)</span></label>
                                    <input
                                        type="number"
                                        min={0}
                                        maxLength={10}
                                        id="total_amt"
                                        name="total_amt"
                                        placeholder="Total Amount"
                                        value={formData.total_amt}
                                        disabled
                                        readOnly
                                        style={{ cursor: 'not-allowed' }}
                                        className={errors.total_amt ? "error" : inputState.total_amt ? "success" : ""}
                                    />
                                    {errors.total_amt && (
                                        <span className="error-message">{errors.total_amt}</span>
                                    )}
                                </div>
                            ) : (
                                <div className="form-field">
                                    <label htmlFor="tds_percentage">TDS Percentage<span className="required">*</span></label>
                                    <input
                                        type="number"
                                        min={0}
                                        maxLength={10}
                                        id="tds_percentage"
                                        name="tds_percentage"
                                        placeholder="TDS Percentage"
                                        onChange={handleInputChange}
                                        value={formData.tds_percentage}
                                        className={errors.tds_percentage ? "error" : inputState.tds_percentage ? "success" : ""}
                                    />
                                    {errors.tds_percentage && (
                                        <span className="error-message">{errors.tds_percentage}</span>
                                    )}
                                </div>
                            )}

                            {Number(formData.rent_amt) >= 50000 && (
                                <>
                                    <div className="form-field">
                                        <label htmlFor="tds_amt">TDS Amount<span className="required">(Read Only)</span></label>
                                        <input
                                            type="number"
                                            min={0}
                                            maxLength={10}
                                            id="tds_amt"
                                            name="tds_amt"
                                            placeholder="TDS Amount"
                                            value={formData.tds_amt}
                                            disabled
                                            readOnly
                                            style={{ cursor: 'not-allowed' }}
                                            className={errors.tds_amt ? "error" : inputState.tds_amt ? "success" : ""}
                                        />
                                        {errors.tds_amt && (
                                            <span className="error-message">{errors.tds_amt}</span>
                                        )}
                                    </div>
                                    <div className="form-field">
                                        <label htmlFor="total_amt">Total Amount<span className="required">(Auto Calculated)</span></label>
                                        <input
                                            type="number"
                                            min={0}
                                            maxLength={10}
                                            id="total_amt"
                                            name="total_amt"
                                            placeholder="Total Amount"
                                            value={formData.total_amt}
                                            disabled
                                            readOnly
                                            style={{ cursor: 'not-allowed' }}
                                            className={errors.total_amt ? "error" : inputState.total_amt ? "success" : ""}
                                        />
                                        {errors.total_amt && (
                                            <span className="error-message">{errors.total_amt}</span>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>

                        <div style={{ marginBottom: '18px' }}>
                            <ViewDocumnetsbilling documentlist={documentlist} />
                        </div>

                        <div className="form-actions">
                            <button type="button" className="btn-secondaryy" onClick={handleClose}>
                                Cancel
                            </button>
                            <button type="submit" className="btn-primaryy" onClick={handleFormSubmitPut} disabled={loading}>
                                {loading ? <Loader size={18} className="spinning" /> : <Check size={18} />}
                                <span>{loading ? 'Approving...' : 'Partially Approve'}</span>
                            </button>
                        </div>
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};
const DynamicRequestVehicleApproval = ({ i, getDynamicRequestData }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const approverLabel = i.employee ? (i.role ? `${i.employee?.name} (${i.role?.role_name})` : i.employee?.name) : i.role?.role_name;

    const totalCharges = Number(i?.vehicle_billing_details?.vehicle_details?.fixed_bill_amt) +
        Number(i?.vehicle_billing_details?.vehicle_details?.extra_amt_above_fix_km) * Number(i.vehicle_billing_details.extra_km);

    const handleFormSubmitPut = async (e) => {
        e.preventDefault();
        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(0);

        const loadingToastId = toast.loading("Loading: Please wait...");

        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            if (progress >= 100) {
                progress = 100;
                clearInterval(interval);
            }
            setUploadProgress(progress);
        }, 100);

        let ApprovalFormData = {
            approval_status: 'approved',
            updated_by: `${sessionStorage.getItem('sitemanagement_emp_id')}`,
            updated_at: new Date().toISOString(),
        };

        try {
            let req = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${i.id}/`, ApprovalFormData);

            if (req.status === 200 || req.status === 201) {
                clearInterval(interval);
                toast.dismiss(loadingToastId);
                toast.success("Vehicle Request Status Updated Successfully");
                await getDynamicRequestData();
                handleClose();
            } else {
                clearInterval(interval);
                setUploadProgress(0);
                toast.error(`Vehicle Request Update Error: ${req.statusText}`);
            }
        } catch (err) {
            clearInterval(interval);
            setUploadProgress(0);
            handleErrorToast(err, loadingToastId);
        } finally {
            clearInterval(interval);
            setTimeout(() => {
                setShowProgressModal(false);
            }, 1500);
            setLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    return (
        <>
            <style>{`
                .drva-info-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 12px 20px;
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 14px 18px;
                    margin-bottom: 18px;
                }
                .drva-info-item {
                    display: flex;
                    flex-direction: column;
                    gap: 3px;
                }
                .drva-info-label {
                    font-size: 11px;
                    font-weight: 600;
                    color: #94a3b8;
                    text-transform: uppercase;
                    letter-spacing: 0.4px;
                }
                .drva-info-value {
                    font-size: 13.5px;
                    color: #1f2937;
                    font-weight: 600;
                }
                .drva-amount-card {
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 8px 18px;
                    margin-bottom: 14px;
                }
                .drva-amount-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 8px 0;
                    font-size: 13.5px;
                    color: #475569;
                }
                .drva-amount-row:not(:last-child) {
                    border-bottom: 1px dashed #e2e8f0;
                }
                .drva-amount-row.total {
                    font-weight: 700;
                    color: #1f2937;
                    font-size: 15px;
                }
            `}</style>

            <button className="model-delete-button-modern actionn-icon" style={{ backgroundColor: "#00000075" }} title={`Vehicle Stage ${i.level} Approval`} onClick={handleShow}>
                <Check size={20} color='white' />
            </button>

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Add" />
            ) : (
                <Modal show={show} onHide={handleClose} dialogClassName="modern-project-modal" size="lg">
                    <Modal.Header className="modern-modal-header">
                        <div className="header-content">
                            <div style={{ color: "white" }}>
                                <CheckCircle size={20} />
                            </div>
                            <div>
                                <Modal.Title>Confirm Stage {i.level} Approval</Modal.Title>
                                <p className="header-subtitle" style={{ color: "white" }}>
                                    {approverLabel} • Vehicle Bill "{i?.vehicle_billing_details?.vehicle_num}"
                                </p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={handleClose}>
                            <X size={20} />
                        </button>
                    </Modal.Header>

                    <Modal.Body className="modern-modal-body">
                        <div className="drva-info-grid">
                            <div className="drva-info-item">
                                <span className="drva-info-label">Project</span>
                                <span className="drva-info-value">{i?.vehicle_billing_details?.vehicle_details?.project_short_name}</span>
                            </div>
                            <div className="drva-info-item">
                                <span className="drva-info-label">Period</span>
                                <span className="drva-info-value">{getMonthName(i?.vehicle_billing_details?.month)}/{i?.vehicle_billing_details?.year}</span>
                            </div>
                            <div className="drva-info-item">
                                <span className="drva-info-label">Vehicle Name</span>
                                <span className="drva-info-value">{i?.vehicle_billing_details?.vehicle_details?.name}</span>
                            </div>
                            <div className="drva-info-item">
                                <span className="drva-info-label">Vehicle Number</span>
                                <span className="drva-info-value">{i?.vehicle_billing_details?.vehicle_details?.vehicle_num}</span>
                            </div>
                        </div>

                        <div className="section-title">
                            <IndianRupee size={18} />
                            <h4>KM Breakdown</h4>
                        </div>

                        <div className="drva-amount-card">
                            <div className="drva-amount-row">
                                <span>Fixed Km</span>
                                <span>{i?.vehicle_billing_details?.fixed_km} KM</span>
                            </div>
                            <div className="drva-amount-row">
                                <span>Extra Km</span>
                                <span>{i?.vehicle_billing_details?.extra_km} KM</span>
                            </div>
                            <div className="drva-amount-row total">
                                <span>Total Km</span>
                                <span>{i?.vehicle_billing_details?.total_km} KM</span>
                            </div>
                        </div>

                        <div className="section-title">
                            <Wallet size={18} />
                            <h4>Amount Breakdown</h4>
                        </div>

                        <div className="drva-amount-card">
                            <div className="drva-amount-row">
                                <span>Fixed Bill Amount</span>
                                <span>{formatCurrencyIndian(i?.vehicle_billing_details?.fixed_bill_amt)}</span>
                            </div>
                            <div className="drva-amount-row">
                                <span>Extra Amount Per KM After Fixed KM</span>
                                <span>{formatCurrencyIndian(i?.vehicle_billing_details?.extra_amt_above_fix_km)}</span>
                            </div>
                            <div className="drva-amount-row">
                                <span>Extra Charges</span>
                                <span>{formatCurrencyIndian(i?.vehicle_billing_details?.extra_charges)}</span>
                            </div>
                            <div className="drva-amount-row total">
                                <span>Total Charges</span>
                                <span>{formatCurrencyIndian(totalCharges)}</span>
                            </div>
                        </div>

                        <div className="form-actions">
                            <button type="button" className="btn-secondaryy" onClick={handleClose}>
                                Cancel
                            </button>
                            <button type="submit" className="btn-primaryy" onClick={handleFormSubmitPut} disabled={loading}>
                                {loading ? <Loader size={18} className="spinning" /> : <Check size={18} />}
                                <span>{loading ? 'Confirming...' : 'Confirm'}</span>
                            </button>
                        </div>

                        <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};

const DynamicRequestVehicleRejection = ({ i, getDynamicRequestData }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => {
        setFormData({ remarks: "" });
        setErrors({});
        setInputState({});
        setShow(true);
    };

    const [formData, setFormData] = useState({
        remarks: "",
    });

    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});

    const approverLabel = i.employee ? (i.role ? `${i.employee?.name} (${i.role?.role_name})` : i.employee?.name) : i.role?.role_name;

    const totalCharges = Number(i?.vehicle_billing_details?.vehicle_details?.fixed_bill_amt) +
        Number(i?.vehicle_billing_details?.vehicle_details?.extra_amt_above_fix_km) * Number(i.vehicle_billing_details.extra_km);

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ["remarks"];
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setInputState({
            ...inputState,
            [name]: value.trim() ? "success" : "",
        });

        setFormData({
            ...formData,
            [name]: value,
        });

        setErrors({
            ...errors,
            [name]: "",
        });
    };

    const handleFormSubmitPut = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(0);

        const loadingToastId = toast.loading("Loading: Please wait...");

        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            if (progress >= 100) {
                progress = 100;
                clearInterval(interval);
            }
            setUploadProgress(progress);
        }, 100);

        let updatedFormData = {
            approval_status: 'rejected',
            remarks: formData.remarks,
            updated_by: sessionStorage.getItem('sitemanagement_emp_id'),
            updated_at: new Date().toISOString(),
        };

        try {
            let res = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${i.id}/`, updatedFormData);

            if (res.status === 200 || res.status === 201) {
                clearInterval(interval);
                toast.dismiss(loadingToastId);
                toast.success("Vehicle Request Status Updated Successfully");
                await getDynamicRequestData();
                handleClose();
            } else {
                clearInterval(interval);
                setUploadProgress(0);
                toast.error(`Vehicle Request Update Error: ${res.statusText}`);
                console.error(res);
            }
        } catch (err) {
            clearInterval(interval);
            setUploadProgress(0);
            handleErrorToast(err, loadingToastId);
        } finally {
            clearInterval(interval);
            setTimeout(() => {
                setShowProgressModal(false);
            }, 1500);
            setLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    return (
        <>
            <style>{`
                .drva-info-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 12px 20px;
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 14px 18px;
                    margin-bottom: 18px;
                }
                .drva-info-item {
                    display: flex;
                    flex-direction: column;
                    gap: 3px;
                }
                .drva-info-label {
                    font-size: 11px;
                    font-weight: 600;
                    color: #94a3b8;
                    text-transform: uppercase;
                    letter-spacing: 0.4px;
                }
                .drva-info-value {
                    font-size: 13.5px;
                    color: #1f2937;
                    font-weight: 600;
                }
                .drva-amount-card {
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 8px 18px;
                    margin-bottom: 14px;
                }
                .drva-amount-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 8px 0;
                    font-size: 13.5px;
                    color: #475569;
                }
                .drva-amount-row:not(:last-child) {
                    border-bottom: 1px dashed #e2e8f0;
                }
                .drva-amount-row.total {
                    font-weight: 700;
                    color: #1f2937;
                    font-size: 15px;
                }
            `}</style>

            <button className="model-delete-button-modern actionn-icon" style={{ backgroundColor: "#00000075" }} title={`Vehicle Stage ${i.level} Rejection`} onClick={handleShow}>
                <X size={20} color='white' />
            </button>

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Reject" />
            ) : (
                <Modal show={show} onHide={handleClose} dialogClassName="modern-project-modal" size="lg">
                    <Modal.Header className="modern-modal-header">
                        <div className="header-content">
                            <div style={{ color: "white" }}>
                                <CrossRed size={24} />
                            </div>
                            <div>
                                <Modal.Title>Reject Stage {i.level} Approval</Modal.Title>
                                <p className="header-subtitle" style={{ color: "white" }}>
                                    {approverLabel} • Vehicle Bill "{i?.vehicle_billing_details?.vehicle_num}"
                                </p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={handleClose}>
                            <X size={20} />
                        </button>
                    </Modal.Header>

                    <Modal.Body className="modern-modal-body">
                        <div className="drva-info-grid">
                            <div className="drva-info-item">
                                <span className="drva-info-label">Project</span>
                                <span className="drva-info-value">{i?.vehicle_billing_details?.vehicle_details?.project_short_name}</span>
                            </div>
                            <div className="drva-info-item">
                                <span className="drva-info-label">Period</span>
                                <span className="drva-info-value">{getMonthName(i?.vehicle_billing_details?.month)}/{i?.vehicle_billing_details?.year}</span>
                            </div>
                            <div className="drva-info-item">
                                <span className="drva-info-label">Vehicle Name</span>
                                <span className="drva-info-value">{i?.vehicle_billing_details?.vehicle_details?.name}</span>
                            </div>
                            <div className="drva-info-item">
                                <span className="drva-info-label">Vehicle Number</span>
                                <span className="drva-info-value">{i?.vehicle_billing_details?.vehicle_details?.vehicle_num}</span>
                            </div>
                        </div>

                        <div className="section-title">
                            <IndianRupee size={18} />
                            <h4>KM Breakdown</h4>
                        </div>

                        <div className="drva-amount-card">
                            <div className="drva-amount-row">
                                <span>Fixed Km</span>
                                <span>{i?.vehicle_billing_details?.fixed_km} KM</span>
                            </div>
                            <div className="drva-amount-row">
                                <span>Extra Km</span>
                                <span>{i?.vehicle_billing_details?.extra_km} KM</span>
                            </div>
                            <div className="drva-amount-row total">
                                <span>Total Km</span>
                                <span>{i?.vehicle_billing_details?.total_km} KM</span>
                            </div>
                        </div>

                        <div className="section-title">
                            <Wallet size={18} />
                            <h4>Amount Breakdown</h4>
                        </div>

                        <div className="drva-amount-card">
                            <div className="drva-amount-row">
                                <span>Fixed Bill Amount</span>
                                <span>{formatCurrencyIndian(i?.vehicle_billing_details?.fixed_bill_amt)}</span>
                            </div>
                            <div className="drva-amount-row">
                                <span>Extra Amount Per KM After Fixed KM</span>
                                <span>{formatCurrencyIndian(i?.vehicle_billing_details?.extra_amt_above_fix_km)}</span>
                            </div>
                            <div className="drva-amount-row">
                                <span>Extra Charges</span>
                                <span>{formatCurrencyIndian(i?.vehicle_billing_details?.extra_charges)}</span>
                            </div>
                            <div className="drva-amount-row total">
                                <span>Total Charges</span>
                                <span>{formatCurrencyIndian(totalCharges)}</span>
                            </div>
                        </div>

                        <div className="form-field full-width" style={{ marginTop: '18px' }}>
                            <label htmlFor="remarks">Rejection Reason<span className="required">*</span></label>
                            <textarea
                                id="remarks"
                                name="remarks"
                                placeholder="Your Reason"
                                onChange={handleInputChange}
                                maxLength={100}
                                value={formData.remarks}
                                rows={3}
                                className={errors.remarks ? "error" : inputState.remarks ? "success" : ""}
                            />
                            {errors.remarks && (
                                <span className="error-message">{errors.remarks}</span>
                            )}
                        </div>

                        <div className="form-actions">
                            <button type="button" className="btn-secondaryy" onClick={handleClose}>
                                Cancel
                            </button>
                            <button type="submit" className="btn-primaryy btn-danger" onClick={handleFormSubmitPut} disabled={loading}>
                                {loading ? <Loader size={18} className="spinning" /> : <X size={18} />}
                                <span>{loading ? 'Submitting...' : 'Reject'}</span>
                            </button>
                        </div>

                        <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};

const DynamicVehicleFinal = ({ i, getVehicleBillingData, documentlist }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const [fileName, setFileName] = useState("");
    const [formData, setFormData] = useState({
        settlement: "",
        settlement_bill: "",
    });

    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = [
            "settlement_bill"
        ];
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setInputState({
            ...inputState,
            [name]: value.trim() ? "success" : "",
        });

        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handleFileChange = (e) => {
        const { name } = e.target;
        const file = e.target.files[0];

        if (file) {
            setFormData({ ...formData, [name]: file, });
            setFileName(file.name);
            setInputState({ ...inputState, [name]: "success" });
        } else {
            setFileName("");
        }
    };

    const handleClear = () => {
        setFormData(prevState => ({
            ...prevState,
            settlement_bill: null
        }));
        setFileName("");
    }

    const totalKm = i?.vehicle_billing_details?.total_km === null
        ? Number(i?.vehicle_billing_details?.extra_km) + Number(i?.vehicle_billing_details?.vehicle_details?.fixed_km)
        : i?.vehicle_billing_details?.total_km;

    const handleFormSubmitPut = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const formDataa = new FormData();
        const symbols = '!@#$%^&*()_-+=';
        const lowercaseLetters = 'abcdefghijklmnopqrstuvwxyz';
        const uppercaseLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const numbers = '0123456789';
        const now = new Date();
        const date = now.toLocaleDateString('en-GB').split('/').reverse().join('-');
        const time = now.toLocaleTimeString('en-GB', { hour12: false }).replace(/:/g, '-');
        const allChars = symbols + lowercaseLetters + uppercaseLetters + numbers;
        let randomCode = '';
        for (let i = 0; i < 8; i++) {
            const randomIndex = Math.floor(Math.random() * allChars.length);
            randomCode += allChars[randomIndex];
        }
        if (formData.settlement_bill) {
            const originalFile = formData?.settlement_bill;
            const customFileName = `Aimantra ${date} ${time}_${randomCode} ${originalFile?.name}`;
            const customFile = new File([originalFile], customFileName, { type: originalFile.type });

            formDataa.append("settlement_bill", customFile);
        }

        formDataa.append("settlement_transaction_id", formData.settlement_transaction_id);

        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(0);

        const loadingToastId = toast.loading("Loading: Please wait...");

        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            if (progress >= 100) {
                progress = 100;
                clearInterval(interval);
            }
            setUploadProgress(progress);
        }, 100);

        try {
            let res = await axios.put(`${BASE_URL}/siteproject/vehiclerentbillingupdate/${i?.vehicle_billing_details?.id}/`, formDataa, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            if (res.status === 200 || res.status === 201) {
                clearInterval(interval);
                toast.dismiss(loadingToastId);
                toast.success("Vehicle Billing Finalized Successfully");
                await getVehicleBillingData();
                handleClose();
            } else {
                clearInterval(interval);
                setUploadProgress(0);
                toast.error(`Vehicle Billing Error: ${res.statusText}`);
            }
        } catch (err) {
            clearInterval(interval);
            setUploadProgress(0);
            handleErrorToast(err, loadingToastId);
        } finally {
            clearInterval(interval);
            setTimeout(() => {
                setShowProgressModal(false);
            }, 1500);
            setLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    return (
        <>
            <style>{`
                .drvf-info-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 12px 20px;
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 14px 18px;
                    margin-bottom: 18px;
                }
                .drvf-info-item {
                    display: flex;
                    flex-direction: column;
                    gap: 3px;
                }
                .drvf-info-label {
                    font-size: 11px;
                    font-weight: 600;
                    color: #94a3b8;
                    text-transform: uppercase;
                    letter-spacing: 0.4px;
                }
                .drvf-info-value {
                    font-size: 13.5px;
                    color: #1f2937;
                    font-weight: 600;
                }
            `}</style>

            <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

            <button className="model-edit-button" title="Account Stage 2 Approval" onClick={handleShow}>
                <CheckGreen />
            </button>

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Finalize" />
            ) : (
                <Modal show={show} onHide={handleClose} dialogClassName="modern-project-modal" size="lg">
                    <Modal.Header className="modern-modal-header">
                        <div className="header-content">
                            <div style={{ color: "white" }}>
                                <CheckCircle size={20} />
                            </div>
                            <div>
                                <Modal.Title>Confirm Final Approval</Modal.Title>
                                <p className="header-subtitle" style={{ color: "white" }}>
                                    Vehicle "{i?.vehicle_billing_details?.vehicle_details?.name}"
                                </p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={handleClose}>
                            <X size={20} />
                        </button>
                    </Modal.Header>

                    <Modal.Body className="modern-modal-body">
                        <div className="drvf-info-grid">
                            <div className="drvf-info-item">
                                <span className="drvf-info-label">Project</span>
                                <span className="drvf-info-value">{i?.vehicle_billing_details?.project__short_name}</span>
                            </div>
                            <div className="drvf-info-item">
                                <span className="drvf-info-label">Period</span>
                                <span className="drvf-info-value">{getMonthName(i.vehicle_billing_details?.month)}/{i?.vehicle_billing_details?.year}</span>
                            </div>
                            <div className="drvf-info-item">
                                <span className="drvf-info-label">Vehicle Details</span>
                                <span className="drvf-info-value">{i?.vehicle_billing_details?.vehicle_details?.name} / {i?.vehicle_billing_details?.vehicle_details?.vehicle_num}</span>
                            </div>
                            <div className="drvf-info-item">
                                <span className="drvf-info-label">Fixed Km</span>
                                <span className="drvf-info-value">{i?.vehicle_billing_details?.fixed_km ? i.vehicle_billing_details?.fixed_km : i?.vehicle_billing_details?.vehicle_details?.fixed_km} km</span>
                            </div>
                            <div className="drvf-info-item">
                                <span className="drvf-info-label">Extra Km</span>
                                <span className="drvf-info-value">{i?.vehicle_billing_details?.extra_km === null ? 0 : i?.vehicle_billing_details?.extra_km} km</span>
                            </div>
                            <div className="drvf-info-item">
                                <span className="drvf-info-label">Total Km</span>
                                <span className="drvf-info-value">{totalKm} km</span>
                            </div>
                            <div className="drvf-info-item">
                                <span className="drvf-info-label">Extra Amount Per KM Above Fixed KM</span>
                                <span className="drvf-info-value">₹{i?.vehicle_billing_details?.vehicle_details?.extra_amt_above_fix_km}</span>
                            </div>
                        </div>

                        <div style={{ marginBottom: '18px' }}>
                            <ViewDocumnetsbilling documentlist={documentlist} />
                        </div>

                        <div className="section-title">
                            <FileText size={18} />
                            <h4>Settlement Details</h4>
                        </div>

                        <div className="form-field full-width">
                            <label htmlFor="settlement_transaction_id">Final Approval Details (Transaction / Cheque no.)</label>
                            <input
                                id="settlement_transaction_id"
                                name="settlement_transaction_id"
                                placeholder="Final Approval Details (Transaction / Cheque no.)"
                                onChange={handleInputChange}
                                value={formData.settlement_transaction_id}
                                className={errors.settlement_transaction_id ? "error" : inputState.settlement_transaction_id ? "success" : ""}
                            />
                            {errors.settlement_transaction_id && (
                                <span className="error-message">{errors.settlement_transaction_id}</span>
                            )}
                        </div>

                        <div className="form-field full-width" style={{ marginTop: '14px' }}>
                            <label htmlFor="settlement_bill">Account Transaction Proof<span className="required">*</span></label>
                            <div className="flex-row" style={{ alignItems: 'center', gap: '12px' }}>
                                <label
                                    htmlFor="settlement_bill"
                                    className={`align-center form-inputss custom-file-upload ${errors.settlement_bill
                                        ? "error"
                                        : inputState.settlement_bill
                                            ? "success"
                                            : ""
                                        }`}
                                >
                                    <div className="svg-field form-input align-center">
                                        <Attachment />
                                    </div>
                                </label>
                                <input
                                    id="settlement_bill"
                                    name="settlement_bill"
                                    type="file"
                                    onChange={handleFileChange}
                                    onFocus={handleClear}
                                    className="file-inputs align-center"
                                    style={{ display: "none", position: "relative" }}
                                />
                                {fileName && <p style={{ margin: "0px" }}>{fileName}</p>}
                                {formData.settlement_bill && (
                                    <button type="button" className="btn-secondaryy" onClick={handleClear}>Clear</button>
                                )}
                            </div>
                            {errors.settlement_bill && (
                                <span className="error-message">{errors.settlement_bill}</span>
                            )}
                        </div>

                        <div className="form-actions">
                            <button type="button" className="btn-secondaryy" onClick={handleClose}>
                                Cancel
                            </button>
                            <button type="submit" className="btn-primaryy" onClick={handleFormSubmitPut} disabled={loading}>
                                {loading ? <Loader size={18} className="spinning" /> : <Check size={18} />}
                                <span>{loading ? 'Approving...' : 'Approve'}</span>
                            </button>
                        </div>
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};

const DynamicRequestVehicleEditApproval = ({ i, getDynamicRequestData, documentlist }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const [maxlimit, setMaxlimit] = useState('')
    const [maxlimittds, setMaxlimittds] = useState('')

    const handleClose = () => { setShow(false) };
    const handleShow = () => {
        setShow(true)
        const filteredData = { ...i };
        delete filteredData.vehicle_billing_details.approval_stages_data;
        setFormData(filteredData);
        setErrors({});
    };

    const [formData, setFormData] = useState({
        fixed_km: '',
        fixed_bill_amt: '',
        extra_amt_above_fix_km: i?.vehicle_billing_details?.vehicle_details?.extra_amt_above_fix_km,
        extra_charges: '',
        extra_amount: "",
        extra_km: "0",
        total_amount: "",
        total_km: "0",
        settlement_bill: null,
        approval_stages_data: {}
    });
    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});

    const approverLabel = i.employee ? (i.role ? `${i.employee?.name} (${i.role?.role_name})` : i.employee?.name) : i.role?.role_name;

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ['extra_km']
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const fallbackFixedKm = i?.vehicle_billing_details?.extra_km
        ?? i?.vehicle_billing_details?.vehicle_details?.extra_km
        ?? 0;

    const fallbackFixedAmount = i?.vehicle_billing_details?.vehicle_details?.fixed_bill_amt
        ?? 0;

    const EXTRAkM = Number(i.vehicle_billing_details?.extra_km ? i.vehicle_billing_details?.extra_km : fallbackFixedKm)

    const extra_amount = EXTRAkM * Number(i.vehicle_billing_details?.vehicle_details?.extra_amt_above_fix_km)

    const totalBillAMount = Number(i.vehicle_billing_details?.vehicle_details?.fixed_bill_amt ?? fallbackFixedAmount) + extra_amount;

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        const fallbackFixedKm = i?.vehicle_billing_details?.fixed_km
            ?? i?.vehicle_billing_details?.vehicle_details?.fixed_km
            ?? 0;

        const fallbackFixedAmount = i?.vehicle_billing_details?.vehicle_details?.fixed_bill_amt
            ?? i?.vehicle_billing_details?.fixed_bill_amt
            ?? 0;

        setInputState((prev) => ({
            ...prev,
            [name]: value.trim() ? "success" : "",
        }));

        const updatedFormData = {
            ...formData,
            [name]: value,
        };

        if (name === "extra_km") {
            const extra_km = Number(value || 0);
            const fixed_km = Number(formData.fixed_km || fallbackFixedKm);
            const extra_amount_perKm = Number(formData?.vehicle_billing_details.extra_amt_above_fix_km || 0);
            const fixed_bill_amt = Number(formData.fixed_bill_amt || fallbackFixedAmount);
            const total_km = extra_km + fixed_km;
            const extra_amount = extra_km * extra_amount_perKm;
            const total_amount = fixed_bill_amt + extra_amount;

            updatedFormData.total_km = total_km;
            updatedFormData.extra_charges = extra_amount;
            updatedFormData.total_amount = total_amount;

            setInputState((prev) => ({
                ...prev,
                total_km: total_km ? "success" : "",
                extra_charges: extra_amount ? "success" : "",
                total_amount: total_amount ? "success" : "",
            }));

            setErrors((prevErrors) => {
                const { total_km, extra_charges, total_amount, ...rest } = prevErrors;
                return rest;
            });
        }

        setFormData(updatedFormData);

        if (errors[name]) {
            setErrors((prevErrors) => {
                const { [name]: removedError, ...restErrors } = prevErrors;
                return restErrors;
            });
        }
    };

    const handleFormSubmitPut = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        let ApprovalFormData = {
            approval_status: 'approved',
            updated_at: new Date().toISOString(),
            updated_by: sessionStorage.getItem('sitemanagement_emp_id')
        };

        const { settlement_bill, ...dataToSubmit } = formData;

        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(0);

        const loadingToastId = toast.loading("Loading: Please wait...");

        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            if (progress >= 100) {
                progress = 100;
                clearInterval(interval);
            }
            setUploadProgress(progress);
        }, 100);

        try {
            let res = await axios.put(`${BASE_URL}/siteproject/vehiclerentbillingupdate/${i?.vehicle_billing_details?.id}/`, dataToSubmit);

            if (res.status === 200 || res.status === 201) {
                let req = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${i.id}/`, ApprovalFormData);
                if (req.status === 200 || req.status === 201) {
                    clearInterval(interval);
                    toast.dismiss(loadingToastId);
                    toast.success("Vehicle Billing Updated Successfully");
                    await getDynamicRequestData();
                    handleClose();
                } else {
                    clearInterval(interval);
                    setUploadProgress(0);
                    toast.error(`Vehicle Billing Update Error: ${req.statusText}`);
                    console.error(req);
                }
            } else {
                clearInterval(interval);
                setUploadProgress(0);
                toast.error(`Vehicle Billing Error: ${res.statusText}`);
            }
        } catch (err) {
            clearInterval(interval);
            setUploadProgress(0);
            handleErrorToast(err, loadingToastId);
        } finally {
            clearInterval(interval);
            setTimeout(() => {
                setShowProgressModal(false);
            }, 1500);
            setLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    return (
        <>
            <style>{`
                .drvea-info-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 12px 20px;
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 14px 18px;
                    margin-bottom: 18px;
                }
                .drvea-info-item {
                    display: flex;
                    flex-direction: column;
                    gap: 3px;
                }
                .drvea-info-label {
                    font-size: 11px;
                    font-weight: 600;
                    color: #94a3b8;
                    text-transform: uppercase;
                    letter-spacing: 0.4px;
                }
                .drvea-info-value {
                    font-size: 13.5px;
                    color: #1f2937;
                    font-weight: 600;
                }
                .drvea-fields-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 16px 20px;
                }
            `}</style>

            <button className="model-delete-button-modern actionn-icon" style={{ backgroundColor: "#00000075", color: "white" }} title={`Vehicle Stage ${i.level} Editable Approval`} onClick={handleShow}>
                !
            </button>

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Update" />
            ) : (
                <Modal show={show} onHide={handleClose} dialogClassName="modern-project-modal" size="lg">
                    <Modal.Header className="modern-modal-header">
                        <div className="header-content">
                            <div style={{ color: "white" }}>
                                <WarnYellow size={20} />
                            </div>
                            <div>
                                <Modal.Title>Partial Approve Stage {i.level} Approval</Modal.Title>
                                <p className="header-subtitle" style={{ color: "white" }}>
                                    {approverLabel} • Vehicle Bill "{i?.vehicle_billing_details?.vehicle_num}"
                                </p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={handleClose}>
                            <X size={20} />
                        </button>
                    </Modal.Header>

                    <Modal.Body className="modern-modal-body">
                        <div className="drvea-info-grid">
                            <div className="drvea-info-item">
                                <span className="drvea-info-label">Project</span>
                                <span className="drvea-info-value">{i?.vehicle_billing_details?.project__short_name}</span>
                            </div>
                            <div className="drvea-info-item">
                                <span className="drvea-info-label">Period</span>
                                <span className="drvea-info-value">{getMonthName(i.vehicle_billing_details?.month)}/{i?.vehicle_billing_details?.year}</span>
                            </div>
                            <div className="drvea-info-item">
                                <span className="drvea-info-label">Vehicle Details</span>
                                <span className="drvea-info-value">{i?.vehicle_billing_details?.vehicle_details?.name} / {i?.vehicle_billing_details?.vehicle_details?.vehicle_num}</span>
                            </div>
                        </div>

                        <div className="section-title">
                            <IndianRupee size={18} />
                            <h4>Editable KM & Amount</h4>
                        </div>

                        <div className="drvea-fields-grid">
                            <div className="form-field">
                                <label htmlFor="fixed_km">Fixed Km</label>
                                <input
                                    id='fixed_km'
                                    type="number"
                                    min={0}
                                    max={i?.vehicle_billing_details?.fixed_km === null ? 0 : i?.vehicle_billing_details?.fixed_km}
                                    name="fixed_km"
                                    placeholder={i?.vehicle_billing_details?.fixed_km === null ? i?.vehicle_billing_details?.vehicle_details.fixed_km : i?.vehicle_billing_details?.fixed_km}
                                    value={formData?.fixed_km}
                                    onChange={handleInputChange}
                                    disabled
                                    className={errors?.fixed_km ? 'error' : ''}
                                />
                            </div>

                            <div className="form-field">
                                <label htmlFor="extra_km">Extra Km<span className="required">*</span></label>
                                <input
                                    id='extra_km'
                                    type="number"
                                    min={0}
                                    max={i?.vehicle_billing_details?.extra_km === null ? 0 : i?.vehicle_billing_details?.extra_km}
                                    name="extra_km"
                                    placeholder={formData.extra_km !== "" ? i?.vehicle_billing_details?.extra_km === null ? 0 : i?.vehicle_billing_details?.extra_km : "0"}
                                    value={formData.extra_km}
                                    onChange={handleInputChange}
                                    className={errors?.extra_km ? 'error' : inputState.extra_km ? 'success' : ''}
                                />
                                {errors?.extra_km && <span className="error-message">{errors?.extra_km}</span>}
                            </div>

                            <div className="form-field">
                                <label htmlFor="total_km">Total Km</label>
                                <input
                                    id='total_km'
                                    type="number"
                                    min={0}
                                    max={i?.vehicle_billing_details?.total_km ? i.vehicle_billing_details?.total_km : i?.vehicle_billing_details?.vehicle_details?.total_km}
                                    name="total_km"
                                    placeholder={
                                        i.vehicle_billing_details
                                            ? i.vehicle_billing_details.total_km === null
                                                ? Number(i.vehicle_billing_details.extra_km) + Number(i.vehicle_billing_details?.fixed_km || 0)
                                                : Number(i.vehicle_billing_details.total_km)
                                            : ''
                                    }
                                    value={formData?.total_km}
                                    onChange={handleInputChange}
                                    disabled
                                    style={{ fontWeight: "bold" }}
                                    className={errors?.total_km ? 'error' : ''}
                                />
                            </div>

                            <div className="form-field">
                                <label htmlFor="fixed_bill_amt">Fixed Bill Amount</label>
                                <input
                                    id='fixed_bill_amt'
                                    type="number"
                                    min={0}
                                    max={i?.vehicle_billing_details?.fixed_bill_amt ? i.vehicle_billing_details?.fixed_bill_amt : i?.vehicle_billing_details?.vehicle_details?.fixed_bill_amt}
                                    name="fixed_bill_amt"
                                    placeholder={formatCurrencyIndian(i?.vehicle_billing_details?.vehicle_details?.fixed_bill_amt)}
                                    value={formatCurrencyIndian(formData?.fixed_bill_amt)}
                                    onChange={handleInputChange}
                                    disabled
                                    className={errors?.fixed_bill_amt ? 'error' : ''}
                                />
                            </div>

                            <div className="form-field">
                                <label>Extra Amount Per KM After Fixed KM</label>
                                <input
                                    type="text"
                                    disabled
                                    value={formatCurrencyIndian(i?.vehicle_billing_details?.extra_amt_above_fix_km)}
                                />
                            </div>

                            <div className="form-field">
                                <label htmlFor="extra_charges">Extra Charges</label>
                                <input
                                    id='extra_charges'
                                    type="number"
                                    min={0}
                                    placeholder={i?.vehicle_billing_details?.extra_charges === null ? formatCurrencyIndian(i?.vehicle_billing_details?.vehicle_details.extra_charges) : formatCurrencyIndian(i?.vehicle_billing_details?.extra_charges)}
                                    name="extra_charges"
                                    value={formData.extra_charges}
                                    disabled
                                    className={errors.extra_charges ? 'error' : ''}
                                />
                            </div>

                            <div className="form-field">
                                <label htmlFor="total_amount">Total Charges</label>
                                <input
                                    id='total_amount'
                                    type="number"
                                    min={0}
                                    name="total_amount"
                                    placeholder={formatCurrencyIndian(totalBillAMount)}
                                    value={formData?.total_amount}
                                    disabled
                                    style={{ fontWeight: "bold" }}
                                    className={errors.total_amount ? 'error' : ''}
                                />
                            </div>
                        </div>

                        <div className="form-actions">
                            <button type="button" className="btn-secondaryy" onClick={handleClose}>
                                Cancel
                            </button>
                            <button type="submit" className="btn-primaryy" onClick={handleFormSubmitPut} disabled={loading}>
                                {loading ? <Loader size={18} className="spinning" /> : <Check size={18} />}
                                <span>{loading ? 'Approving...' : 'Partially Approve'}</span>
                            </button>
                        </div>

                        <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};
const DynamicRequestboqApproval = ({ i, getDynamicRequestData }) => {
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => {
        setFormData({
            approved_amount: i.tender_amount_details?.change_log?.length > 1 ? i.tender_amount_details?.approved_amount : i.tender_amount_details?.amount,
        })
        setShow(true)
    };

    const [formData, setFormData] = useState({
        approved_amount: i.tender_amount_details?.change_log?.length > 1 ? i.tender_amount_details?.approved_amount : i.tender_amount_details?.amount,
    });

    // console.log("formData- ", formData)
    const handleFormSubmitPut = async (e) => {
        e.preventDefault();
        const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic
        let TenderFormData = {}

        TenderFormData = {
            approved_amount: formData.amount,
        };

        let ApprovalFormData = {
            approval_status: 'approved',
            updated_at: new Date().toISOString(),
            updated_by: sessionStorage.getItem('sitemanagement_emp_id')
        };

        // console.log("TenderFormData")
        // console.log(TenderFormData)
        // console.log("ApprovalFormData")
        // console.log(ApprovalFormData)

        try {
            let tender = await axios.put(`${BASE_URL}/siteproject/tenderRequestAmount/${i.tender_amount_details?.id}/`,
                TenderFormData,
            );

            if (tender.status === 200 || tender.status === 201) {
                toast.success("Tender Updated Successfully");
                let req = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${i.id}/`,
                    ApprovalFormData,
                );
                if (req.status === 200 || req.status === 201) {
                    toast.success("Tender Request Status Updated Successfully");
                    await getDynamicRequestData();
                } else {
                    toast.error(`Tender Request Update Error: ${req.statusText}`);
                    console.log('Tender Request Update Error:')
                    console.error(req);
                }
            } else {
                toast.error(`Tender Update Error: ${tender.statusText}`);
                console.log('Tender Update Error:')
                console.error(tender);
            }
        } catch (err) {
            handleErrorToast(err, loadingToastId)
        } finally {
            toast.dismiss(loadingToastId);
        }
    };
    console.log("i-", i)

    return (
        <>
            <button className="model-edit-button" title={`Tender Stage ${i.level} Approval`} onClick={handleShow}>
                <CheckGreen />
            </button>
            <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
            <Modal show={show} onHide={handleClose} dialogClassName="request-leave">
                <Modal.Body>
                    <div className="parent-div">
                        <div className="bdy-div">
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">Confirm Stage {i.level} {i.employee ? (i.role ? `${i.employee?.name}(${i.role?.role_name})` : `${i.employee?.name}`) : i.role?.role_name} Approval of Imprest "{i.imprest_details?.imprest_name}" </h6>
                            <div className="flex-row">
                                <label htmlFor="date" className="form-labels font-weight500 announce-date font-weight400  font-size-heading">
                                    Bank Name :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.tender_amount_details?.bank_name}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label htmlFor="total" className="form-labels font-weight500 announce-date font-weight400 font-size-heading">
                                    Request Type :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.tender_amount_details?.request_type}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="no_of_days"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i.tender_amount_details?.date)}
                                </div>
                            </div>

                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Expiry Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i.tender_amount_details?.expiry_date)}
                                </div>
                            </div>

                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Release Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i.tender_amount_details?.release_date)}
                                </div>
                            </div>
                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Document:
                                </label>
                                <div className="form-labels announce-date font-size-heading">

                                    {i.tender_amount_details?.document ? <a href={i.tender_amount_details?.document} target="blank">
                                        <div className="flex-row justify-center">
                                            <DocumentIcon />
                                        </div>
                                    </a> : "No Document Attached"}
                                </div>
                            </div>
                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Bid:
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.tender_amount_details?.bid}

                                </div>
                            </div>


                            <div className="button-models">
                                <button className="model-button model-button-cancel  font-weight500   " onClick={handleClose}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="model-button   font-weight500    model-button-leave  font-weight500    font-size-heading"
                                    onClick={handleFormSubmitPut}
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal >
        </>
    );
};
const DynamicRequestTenderApproval = ({ i, getDynamicRequestData, dynamicStages, dynamicApprovals, level }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const dynamicApprovalLevel = dynamicApprovals?.find(stage => stage.level === level);
    const dynamicStagesLevel = dynamicStages?.find(stage => stage.level === level);

    const [formData, setFormData] = useState({
        level: level,
        format_for: "BOQ",
        role: "",
        employee: "",
        request: i.id,
        approval_status: "approved",
        permissions: dynamicStagesLevel?.permissions || "",
    });

    const dynamicApprovalLevelfilter = dynamicApprovals?.find(stage => stage.level === level);

    const approverLabel = dynamicStagesLevel?.employee && dynamicStagesLevel?.employee !== "null"
        ? dynamicStagesLevel?.employee_details?.name
        : dynamicStagesLevel?.role_name;

    const handleFormSubmitPost = async (e) => {
        e.preventDefault();
        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(0);

        const loadingToastId = toast.loading("Loading: Please wait...");

        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            if (progress >= 100) {
                progress = 100;
                clearInterval(interval);
            }
            setUploadProgress(progress);
        }, 100);

        try {
            let res = await axios.post(`${BASE_URL}/siteproject/dynamicapprovals/`, formData);

            if (res.status === 200 || res.status === 201) {
                clearInterval(interval);
                toast.dismiss(loadingToastId);
                toast.success("BOQ Updated Successfully");
                await getDynamicRequestData();
                handleClose();
            } else {
                clearInterval(interval);
                setUploadProgress(0);
                toast.error(`BOQ Update Error: ${res.statusText}`);
            }
        } catch (err) {
            clearInterval(interval);
            setUploadProgress(0);
            handleErrorToast(err, loadingToastId);
        } finally {
            clearInterval(interval);
            setTimeout(() => {
                setShowProgressModal(false);
            }, 1500);
            setLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    const handleFormSubmitPut = async (e) => {
        e.preventDefault();
        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(0);

        const loadingToastId = toast.loading("Loading: Please wait...");

        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            if (progress >= 100) {
                progress = 100;
                clearInterval(interval);
            }
            setUploadProgress(progress);
        }, 100);

        try {
            let res = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${dynamicApprovalLevelfilter?.id}/`, formData);

            if (res.status === 200 || res.status === 201) {
                clearInterval(interval);
                toast.dismiss(loadingToastId);
                toast.success("BOQ Updated Successfully");
                await getDynamicRequestData();
                handleClose();
            } else {
                clearInterval(interval);
                setUploadProgress(0);
                toast.error(`BOQ Update Error: ${res.statusText}`);
            }
        } catch (err) {
            clearInterval(interval);
            setUploadProgress(0);
            handleErrorToast(err, loadingToastId);
        } finally {
            clearInterval(interval);
            setTimeout(() => {
                setShowProgressModal(false);
            }, 1500);
            setLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    useEffect(() => {
        if (!dynamicStagesLevel) return; // guard: data abhi ready nahi

        if (dynamicStagesLevel?.role) {
            setFormData((prevFormData) => ({
                ...prevFormData,
                role: dynamicStagesLevel.role,
            }));
        } else {
            setFormData((prevFormData) => ({
                ...prevFormData,
                employee: dynamicStagesLevel.employee,
            }));
        }
    }, [dynamicStagesLevel]);

    return (
        <>
            <style>{`
                .drva-info-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 12px 20px;
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 14px 18px;
                    margin-bottom: 18px;
                }
                .drva-info-item {
                    display: flex;
                    flex-direction: column;
                    gap: 3px;
                }
                .drva-info-label {
                    font-size: 11px;
                    font-weight: 600;
                    color: #94a3b8;
                    text-transform: uppercase;
                    letter-spacing: 0.4px;
                }
                .drva-info-value {
                    font-size: 13.5px;
                    color: #1f2937;
                    font-weight: 600;
                }
                .drva-amount-card {
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 8px 18px;
                    margin-bottom: 14px;
                }
                .drva-amount-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 8px 0;
                    font-size: 13.5px;
                    color: #475569;
                }
                .drva-amount-row:not(:last-child) {
                    border-bottom: 1px dashed #e2e8f0;
                }
                .drva-amount-row.total {
                    font-weight: 700;
                    color: #1f2937;
                    font-size: 15px;
                }
            `}</style>

            <button className="model-delete-button-modern actionn-icon" style={{ backgroundColor: "#00000075" }} title={`Tender Stage ${level} Approval`} onClick={handleShow}>
                <Check size={20} color='white' />
            </button>

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Add" />
            ) : (
                <Modal show={show} onHide={handleClose} dialogClassName="modern-project-modal" size="lg">
                    <Modal.Header className="modern-modal-header">
                        <div className="header-content">
                            <div style={{ color: "white" }}>
                                <CheckCircle size={20} />
                            </div>
                            <div>
                                <Modal.Title>Confirm Stage {level} Approval</Modal.Title>
                                <p className="header-subtitle" style={{ color: "white" }}>
                                    {approverLabel} • BOQ "{i?.project_short_name}"
                                </p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={handleClose}>
                            <X size={20} />
                        </button>
                    </Modal.Header>

                    <Modal.Body className="modern-modal-body">
                        <div className="drva-info-grid">
                            <div className="drva-info-item">
                                <span className="drva-info-label">Project Code</span>
                                <span className="drva-info-value">{i?.site_prcode}</span>
                            </div>
                            <div className="drva-info-item">
                                <span className="drva-info-label">Project Name</span>
                                <span className="drva-info-value">{i?.project_short_name}</span>
                            </div>
                            <div className="drva-info-item">
                                <span className="drva-info-label">Authority Engineer</span>
                                <span className="drva-info-value">{i?.authority_engineer_name || "N/A"}</span>
                            </div>
                            <div className="drva-info-item">
                                <span className="drva-info-label">Stage</span>
                                <span className="drva-info-value">Level {level} • {approverLabel}</span>
                            </div>
                        </div>

                        <div className="section-title">
                            <FileText size={18} />
                            <h4>BOQ Details</h4>
                        </div>

                        <div className="drva-amount-card">
                            <div className="drva-amount-row">
                                <span>Lead</span>
                                <span>{i?.lead || "N/A"}</span>
                            </div>
                            <div className="drva-amount-row">
                                <span>JV</span>
                                <span>{i?.jv || "N/A"} {i?.jv_share ? `(${i.jv_share}%)` : ""}</span>
                            </div>
                            <div className="drva-amount-row">
                                <span>Association</span>
                                <span>{i?.association || "N/A"}</span>
                            </div>
                            <div className="drva-amount-row total">
                                <span>Our Share</span>
                                <span>{i?.our_share}%</span>
                            </div>
                        </div>

                        <div className="form-actions">
                            <button type="button" className="btn-secondaryy" onClick={handleClose}>
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn-primaryy"
                                onClick={dynamicApprovalLevelfilter ? handleFormSubmitPut : handleFormSubmitPost}
                                disabled={loading}
                            >
                                {loading ? <Loader size={18} className="spinning" /> : <Check size={18} />}
                                <span>{loading ? 'Confirming...' : 'Confirm'}</span>
                            </button>
                        </div>

                        <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};

const DynamicRequestTenderEditApproval = ({ i, getDynamicRequestData }) => {
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => {
        setFormData({
            approved_amount: i.tender_amount_details?.change_log?.length > 1 ? i.tender_amount_details?.approved_amount : i.tender_amount_details?.amount,
            amount_change_reason: "",
        })
        setShow(true)
    };

    const [formData, setFormData] = useState({
        approved_amount: i.tender_amount_details?.change_log?.length > 1 ? i.tender_amount_details?.approved_amount : i.tender_amount_details?.amount,
        amount_change_reason: "",
    });

    //******************Validation Start********************* */
    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ["amount_change_reason"];
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // const handleInputChange = (e) => {
    //     const { name, value } = e.target;

    //     if (name.startsWith('approved_')) {

    //         // For individual expenses like transportation, accommodation, fooding, etc.
    //         if (!name.startsWith('approved_other_')) {
    //             const itemAmountKey = name.replace('approved_', '');
    //             const currentValue = i[itemAmountKey] !== undefined && i[itemAmountKey] !== null ? i[itemAmountKey] : 0;
    //             const maxLength = getNumberLength(currentValue);

    //             // Ensure the input value does not exceed the allowed length
    //             if (value.length <= maxLength) {
    //                 const numericValue = value.trim() ? parseInt(value, 10) : 0;

    //                 // Check if the numeric value is less than or equal to the current value
    //                 if (numericValue <= currentValue) {
    //                     setInputState({
    //                         ...inputState,
    //                         [name]: value.trim() ? "success" : "",
    //                     });

    //                     setFormData({
    //                         ...formData,
    //                         [name]: value,
    //                     });

    //                     // Clear any previous error
    //                     setErrors({
    //                         ...errors,
    //                         [name]: "",
    //                     });
    //                 } else {
    //                     // Handle case where the numeric value exceeds the current value
    //                     setErrors({
    //                         ...errors,
    //                         [name]: `Value must be less than or equal to ${currentValue}`,
    //                     });
    //                 }
    //             } else {
    //                 // Handle case where input length exceeds the allowed length
    //                 setErrors({
    //                     ...errors,
    //                     [name]: `Maximum allowed length is ${maxLength} digits`,
    //                 });
    //             }

    //         } else {
    //             // For entries in approved_other_details
    //             const index = parseInt(name.replace('approved_other_', ''), 10);
    //             const currentOther = i.imprest_details?.other_details[index];
    //             const currentValue = currentOther.amount !== undefined && currentOther.amount !== null ? currentOther.amount : 0;
    //             const maxLength = getNumberLength(currentValue);

    //             // Ensure the input value does not exceed the allowed length
    //             if (value.length <= maxLength) {
    //                 const numericValue = value.trim() ? parseInt(value, 10) : 0;

    //                 // Check if the numeric value is less than or equal to the current value
    //                 if (numericValue <= currentValue) {
    //                     const newOtherDetails = [...formData.other_details];
    //                     newOtherDetails[index].approved_other = value;

    //                     setInputState({
    //                         ...inputState,
    //                         [name]: value.trim() ? "success" : "",
    //                     });

    //                     setFormData({
    //                         ...formData,
    //                         other_details: newOtherDetails,
    //                     });

    //                     // Clear any previous error
    //                     setErrors({
    //                         ...errors,
    //                         [name]: "",
    //                     });
    //                 } else {
    //                     // Handle case where the numeric value exceeds the current value
    //                     setErrors({
    //                         ...errors,
    //                         [name]: `Value must be less than or equal to ${currentValue}`,
    //                     });
    //                 }
    //             } else {
    //                 // Handle case where input length exceeds the allowed length
    //                 setErrors({
    //                     ...errors,
    //                     [name]: `Maximum allowed length is ${maxLength} digits`,
    //                 });
    //             }
    //         }

    //     } else {
    //         // For textarea or other fields without length validation
    //         setInputState({
    //             ...inputState,
    //             [name]: value.trim() ? "success" : "",
    //         });

    //         setFormData({
    //             ...formData,
    //             [name]: value,
    //         });
    //         setErrors({
    //             ...errors,
    //             [name]: "",
    //         });
    //     }
    // };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === "approved_amount") {
            const currentValue = i.tender_amount_details?.amount ?? 0;
            const maxLength = getNumberLength(currentValue);
            const numericValue = value.trim() ? parseInt(value, 10) : 0;

            if (value.length <= maxLength) {
                if (numericValue <= currentValue) {
                    setInputState({
                        ...inputState,
                        [name]: value.trim() ? "green" : "",
                    });

                    setFormData({
                        ...formData,
                        [name]: value,
                    });

                    setErrors({
                        ...errors,
                        [name]: "",
                    });
                } else {
                    setErrors({
                        ...errors,
                        [name]: `Value must be less than or equal to ${currentValue}`,
                    });
                }
            } else {
                setErrors({
                    ...errors,
                    [name]: `Maximum allowed length is ${maxLength} digits`,
                });
            }
        } else {
            // For fields like amount_change_reason
            setInputState({
                ...inputState,
                [name]: value.trim() ? "green" : "",
            });

            setFormData({
                ...formData,
                [name]: value,
            });

            setErrors({
                ...errors,
                [name]: "",
            });
        }
    };



    function getNumberLength(value) {
        return value?.toString()?.length;
    }

    const handleFormSubmitPut = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            const loadingToastId = toast.loading("Loading: Please wait...");
            let TenderFormData = {}

            TenderFormData = {
                approved_amount: formData.amount,
                amount_change_reason: formData.amount_change_reason,
            };

            let ApprovalFormData = {
                approval_status: 'approved',
                updated_at: new Date().toISOString(),
                updated_by: sessionStorage.getItem('sitemanagement_emp_id')
            };


            try {
                let tender = await axios.put(`${BASE_URL}/siteproject/tenderRequestAmount/${i.tender_amount_details?.id}/`,
                    TenderFormData,
                );

                if (tender.status === 200 || tender.status === 201) {
                    toast.success("Tender Updated Successfully");
                    let req = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${i.id}/`, ApprovalFormData,);

                    if (req.status === 200 || req.status === 201) {
                        toast.success("Tender Request Status Updated Successfully");
                        await getDynamicRequestData();
                    } else {
                        toast.error(`Tender Request Update Error: ${req.statusText}`);
                        console.log('Tender Request Update Error:')
                        console.error(req);
                    }
                } else {
                    toast.error(`Tender Update Error: ${tender.statusText}`);
                    console.log('Tender Update Error:')
                    console.error(tender);
                }
            } catch (err) {
                handleErrorToast(err, loadingToastId);
            } finally {
                toast.dismiss(loadingToastId);
            }
        }
    };

    return (
        <>
            <button className="model-partial-button" title={`Tender Stage ${i.level} Editable Approval`} onClick={handleShow}>
                <WarnYellow />
            </button>
            <Modal show={show} onHide={handleClose} dialogClassName="request-leave width-40vw">
                <Modal.Body>
                    <div className="parent-div">
                        <div className="bdy-div">
                            <h1 className="task-Tab-heading align-center font-weight500 font-size-heading">Give Stage {i.level} {i.employee ? (i.role ? `${i.employee?.name}(${i.role?.role_name})` : `${i.employee?.name}`) : i.role?.role_name} Approvable Amount for Tender : "{i.imprest_details?.imprest_name}" </h1>

                            <div className="flex-row">
                                <label htmlFor="date" className="form-labels font-weight500 announce-date font-weight400  font-size-heading">
                                    Bank Name :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.tender_amount_details?.bank_name}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label htmlFor="total" className="form-labels font-weight500 announce-date font-weight400 font-size-heading">
                                    Request Type :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.tender_amount_details?.request_type}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="no_of_days"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i.tender_amount_details?.date)}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Expiry Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i.tender_amount_details?.expiry_date)}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Release Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i.tender_amount_details?.release_date)}
                                </div>
                            </div>
                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Document:
                                </label>
                                <div className="form-labels announce-date font-size-heading">

                                    {i.tender_amount_details?.document ? <a href={i.tender_amount_details?.document} target="blank">
                                        <div className="flex-row justify-center">
                                            <DocumentIcon />
                                        </div>
                                    </a> : "No Document Attached"}
                                </div>
                            </div>
                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Bid:
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.tender_amount_details?.bid}

                                </div>
                            </div>


                            <div title="Rent Amount" className="flex-column">
                                <label htmlFor="approved_amount" className="form-labels  font-weight500    font-size-subheading">
                                    Amount Approval<span className="required">*</span>
                                </label>
                                <input
                                    type="number"
                                    min={0}
                                    max={i.amount}
                                    maxLength={10}
                                    id="approved_amount"
                                    name="approved_amount"
                                    placeholder="Basic Amount."
                                    onChange={handleInputChange}
                                    value={formData.approved_amount}
                                    className={`form-input ${errors.approved_amount
                                        ? "error"
                                        : inputState.approved_amount
                                            ? "success"
                                            : ""
                                        }`}
                                />
                                {errors.approved_amount && (
                                    <span className="error-message font-size-text ">{errors.approved_amount}</span>
                                )}
                            </div>

                            <div className="flex-column">
                                <label htmlFor="amount_change_reason" className="form-labels font-weight500 announce-date font-weight400 font-size-heading">
                                    Amount Change Reason<span className="required">*</span>
                                </label>
                                <textarea
                                    id="amount_change_reason"
                                    type="text"
                                    name="amount_change_reason"
                                    placeholder="Your Reason"
                                    onChange={handleInputChange}
                                    maxLength={100}
                                    value={formData.amount_change_reason}
                                    className={`form-input-textarea font-weight400 font-size-subheading ${errors.amount_change_reason ? "error" : inputState.amount_change_reason ? "success" : ""}`}
                                />
                                {errors.amount_change_reason && (
                                    <span className="error-message font-size-text">{errors.amount_change_reason}</span>
                                )}
                            </div>

                            <div className="button-models">
                                <button className="model-button model-button-cancel font-weight500" onClick={handleClose}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="model-button font-weight500 model-button-submit font-size-heading"
                                    onClick={handleFormSubmitPut}
                                >
                                    Submit
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
            </Modal>
        </>
    );
};

const DynamicRequestTenderFinalize = ({ i, getDynamicRequestData }) => {
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => {
        setFormData({
            approved_stationary_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_stationary_item : i.imprest_details?.stationary_item,
            approved_electronic_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_electronic_item : i.imprest_details?.electronic_item,
            approved_grocery: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_grocery : i.imprest_details?.grocery,
            other_details: i.imprest_details?.other_details?.map((other) => ({
                other_name: other.other_name,
                amount: other.amount,
                approved_other: i.imprest_details?.change_log?.length > 1 ? other.approved_other : other.amount,
            })),

            account_approved_final_details: "",
            account_transaction_proof: null,
            imprest_open: true,
        })
        setShow(true)
    };


    const [fileName, setFileName] = useState("");
    const [formData, setFormData] = useState({
        approved_stationary_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_stationary_item : i.imprest_details?.stationary_item,
        approved_electronic_item: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_electronic_item : i.imprest_details?.electronic_item,
        approved_grocery: i.imprest_details?.change_log?.length > 1 ? i.imprest_details?.approved_grocery : i.imprest_details?.grocery,
        other_details: i.imprest_details?.other_details?.map((other) => ({
            other_name: other.other_name,
            amount: other.amount,
            approved_other: i.imprest_details?.change_log?.length > 1 ? other.approved_other : other.amount,
        })),

        account_approved_final_details: "",
        account_transaction_proof: null,
        imprest_open: true,
    });

    const totalApproved =
        i.imprest_details?.change_log?.length > 1
            ?
            (Number(i.imprest_details?.approved_stationary_item) +
                Number(i.imprest_details?.approved_electronic_item) +
                Number(i.imprest_details?.approved_grocery) +
                // Number(i.approved_other)
                (i.imprest_details?.other_details?.length > 0
                    ? i.imprest_details?.other_details?.reduce((total, other) => total + Number(other.approved_other), 0)
                    : 0))
            : (
                Number(formData.approved_stationary_item) +
                Number(formData.approved_electronic_item) +
                Number(formData.approved_grocery) +
                (formData.other_details?.length > 0
                    ? formData.other_details?.reduce((total, other) => total + Number(other.approved_other || 0), 0)
                    : 0)
            );

    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});
    const validateForm = () => {
        const newErrors = {};
        const requiredFields =
            totalApproved === 0 ? [] :
                [
                    "account_approved_final_details",
                    "account_transaction_proof"
                ];
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setInputState({
            ...inputState,
            [name]: value.trim() ? "green" : "",
        });

        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handleFileChange = (e) => {
        const { name } = e.target;
        const file = e.target.files[0];

        if (file) {
            setFormData({ ...formData, [name]: file, });
            setFileName(file.name);
        } else {
            setFileName("");
        }
    };

    const handleClear = () => {
        setFormData(prevState => ({
            ...prevState,
            account_transaction_proof: null
        }));
        setFileName("");

    }

    const handleFormSubmitPut = async (e) => {

        e.preventDefault();
        if (validateForm()) {
            const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic

            const ImprestFormData = new FormData();

            const symbols = '!@#$%^&*()_-+=';
            const lowercaseLetters = 'abcdefghijklmnopqrstuvwxyz';
            const uppercaseLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
            const numbers = '0123456789';
            const now = new Date();
            const date = now.toLocaleDateString('en-GB').split('/').reverse().join('-'); // Format: YYYY-MM-DD
            const time = now.toLocaleTimeString('en-GB', { hour12: false }).replace(/:/g, '-'); // Format: HH-MM-SS
            const allChars = symbols + lowercaseLetters + uppercaseLetters + numbers;
            let randomCode = '';
            for (let i = 0; i < 8; i++) {
                const randomIndex = Math.floor(Math.random() * allChars.length);
                randomCode += allChars[randomIndex];
            }
            if (formData.account_transaction_proof && formData.account_approved_final_details) {
                const originalFile = formData?.account_transaction_proof;
                const customFileName = `Aimantra ${date} ${time}_${randomCode} ${originalFile?.name}`;
                const customFile = new File([originalFile], customFileName, { type: originalFile.type });

                ImprestFormData.append("account_approved_final_details", formData.account_approved_final_details);
                ImprestFormData.append("account_transaction_proof", customFile);
            }
            ImprestFormData.append("imprest_open", formData.imprest_open);

            let ApprovalFormData = {
                approval_status: 'approved',
                updated_at: new Date().toISOString(),
                updated_by: sessionStorage.getItem('sitemanagement_emp_id')
            };

            try {
                let imp = await axios.put(`${BASE_URL}/siteproject/imprest/${i.imprest_details?.id}/`, ImprestFormData, AUTHH, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                });

                if (imp.status === 200 || imp.status === 201) {
                    toast.success("Imprest Updated Successfully");
                    let req = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${i.id}/`, ApprovalFormData,);

                    if (req.status === 200 || req.status === 201) {
                        toast.success("Imprest Request Status Updated Successfully");
                        await getDynamicRequestData();
                    } else {
                        toast.error(`Imprest Request Update Error: ${req.statusText}`);
                        console.log('Imprest Request Update Error:')
                        console.error(req);

                    }
                } else {
                    toast.error(`Imprest Update Error: ${imp.statusText}`);
                    console.log('Imprest Update Error:')
                    console.error(imp);
                }

                // if (imp.status === 200) {
                //     await getDynamicRequestData();
                //     setShow(false);
                // } else {
                //     alert(imp);
                // }
            } catch (err) {
                handleErrorToast(err, loadingToastId)
            } finally {
                toast.dismiss(loadingToastId);
            }
        }
    };

    return (
        <>
            <button className="model-edit-button" title={`Tender Stage ${i.level} Finalize Approval`} onClick={handleShow}>
                <CheckGreen />
            </button>
            <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
            <Modal show={show} onHide={handleClose} dialogClassName="request-leave width-40vw">
                <Modal.Body>
                    <div className="parent-div">

                        <div className="bdy-div">
                            <h6 className="task-Tab-heading align-center font-weight500 font-size-heading">Finalize Stage {i.level} {i.employee ? (i.role ? `${i.employee?.name}(${i.role?.role_name})` : `${i.employee?.name}`) : i.role?.role_name} Approval of Tender "{i.imprest_details?.imprest_name}" </h6>
                            <div className="flex-row">
                                <label htmlFor="date" className="form-labels font-weight500 announce-date font-weight400  font-size-heading">
                                    Bank Name :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.tender_amount_details?.bank_name}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label htmlFor="total" className="form-labels font-weight500 announce-date font-weight400 font-size-heading">
                                    Request Type :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.tender_amount_details?.request_type}
                                </div>
                            </div>

                            <div className="flex-row">
                                <label
                                    htmlFor="no_of_days"
                                    className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                >
                                    Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i.tender_amount_details?.date)}
                                </div>
                            </div>

                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Expiry Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i.tender_amount_details?.expiry_date)}
                                </div>
                            </div>

                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Release Date :
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {formattedDateLong(i.tender_amount_details?.release_date)}
                                </div>
                            </div>
                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Document:
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.tender_amount_details?.document ? <a href={i.tender_amount_details?.document} target="blank">
                                        <div className="flex-row justify-center">
                                            <DocumentIcon />
                                        </div>
                                    </a> : "No Document Attached"}

                                </div>
                            </div>
                            <div className="flex-column">
                                <label htmlFor="remarks" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                    Bid:
                                </label>
                                <div className="form-labels announce-date font-size-heading">
                                    {i.tender_amount_details?.bid}

                                </div>
                            </div>


                            {totalApproved == 0 ? null :
                                <>
                                    <div className="flex-column">
                                        <label
                                            htmlFor="account_approved_final_details"
                                            className="form-labels  font-weight500    announce-date font-weight400  font-size-heading"
                                        >
                                            Final Approval Details (Transaction / Cheque no.)
                                        </label>
                                        <input
                                            id="account_approved_final_details"
                                            name="account_approved_final_details"
                                            placeholder="Final Approval Details (Transaction / Cheque no.)"
                                            onChange={handleInputChange}
                                            value={formData.account_approved_final_details}
                                            className={`form-input ${errors.account_approved_final_details
                                                ? "error"
                                                : inputState.account_approved_final_details
                                                    ? "success"
                                                    : ""
                                                }`}
                                        />
                                        {errors.account_approved_final_details && (
                                            <span className="error-message font-size-text ">{errors.account_approved_final_details}</span>
                                        )}
                                    </div>

                                    <div className="flex-row">
                                        <label htmlFor="" className="form-labels  font-weight500    announce-date font-weight400  font-size-heading">
                                            Account Transaction Proof :
                                        </label>
                                        <div className="flex-column" >
                                            <label
                                                htmlFor="account_transaction_proof"
                                                className={`align-center  form-inputss custom-file-upload 
                                                    ${errors.account_transaction_proof
                                                        ? "error"
                                                        : inputState.account_transaction_proof
                                                            ? "success"
                                                            : ""
                                                    }`}
                                            >
                                                <div className="svg-field form-input align-center">
                                                    <Attachment />
                                                </div>
                                            </label>
                                            <input
                                                id="account_transaction_proof"
                                                name="account_transaction_proof"
                                                type="file"
                                                onChange={handleFileChange}
                                                onFocus={handleClear}
                                                className="file-inputs align-center"
                                                style={{ display: "none", position: "relative" }}
                                            />
                                            <br />
                                            <div className="flex-row justify-evenly">
                                                <div>
                                                    {fileName && <p style={{ margin: "0px" }}>{fileName}</p>}
                                                </div>
                                            </div>
                                        </div>
                                        {formData.account_transaction_proof ?
                                            <div>
                                                <button className="model-button" onClick={handleClear}>Clear</button>
                                            </div>
                                            : null
                                        }
                                        {errors.account_transaction_proof && (
                                            <span className="error-message font-size-text ">{errors.account_transaction_proof}</span>
                                        )}
                                    </div>
                                </>
                            }
                            <br />

                            <div className="button-models">
                                <button className="model-button model-button-cancel  font-weight500   " onClick={handleClose}>
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="model-button   font-weight500    model-button-leave  font-weight500    font-size-heading"
                                    onClick={handleFormSubmitPut}
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>

                </Modal.Body>
            </Modal >
        </>
    );
};

const DynamicRequestTenderRejection = ({ i, getDynamicRequestData, dynamicStages, dynamicApprovals, level }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const dynamicStagesLevel = dynamicStages?.find(stage => stage.level === level);

    const [formData, setFormData] = useState({
        level: level,
        format_for: "BOQ",
        role: "",
        employee: "",
        request: i.id,
        approval_status: "rejected",
        permissions: dynamicStagesLevel?.permissions || "",
        remarks: "",
    });
    const dynamicApprovalLevelfilter = dynamicApprovals?.find(stage => stage.level === level);

    const approverLabel = dynamicStagesLevel?.employee && dynamicStagesLevel?.employee !== "null"
        ? dynamicStagesLevel?.employee_details?.name
        : dynamicStagesLevel?.role_name;

    //******************Valedation Start********************* */

    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ["remarks"];
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setInputState({
            ...inputState,
            [name]: value.trim() ? "green" : "",
        });

        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handleFormSubmitPost = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(0);

        const loadingToastId = toast.loading("Loading: Please wait...");

        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            if (progress >= 100) {
                progress = 100;
                clearInterval(interval);
            }
            setUploadProgress(progress);
        }, 100);

        try {
            let res = await axios.post(`${BASE_URL}/siteproject/dynamicapprovals/`, formData);

            if (res.status === 200 || res.status === 201) {
                clearInterval(interval);
                toast.dismiss(loadingToastId);
                toast.success("BOQ Updated Successfully");
                await getDynamicRequestData();
                handleClose();
            } else {
                clearInterval(interval);
                setUploadProgress(0);
                toast.error(`BOQ Update Error: ${res.statusText}`);
            }
        } catch (err) {
            clearInterval(interval);
            setUploadProgress(0);
            handleErrorToast(err, loadingToastId);
        } finally {
            clearInterval(interval);
            setTimeout(() => {
                setShowProgressModal(false);
            }, 1500);
            setLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    const handleFormSubmitPut = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(0);

        const loadingToastId = toast.loading("Loading: Please wait...");

        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            if (progress >= 100) {
                progress = 100;
                clearInterval(interval);
            }
            setUploadProgress(progress);
        }, 100);

        try {
            let res = await axios.put(`${BASE_URL}/siteproject/dynamicapprovals/${dynamicApprovalLevelfilter?.id}/`, formData);

            if (res.status === 200 || res.status === 201) {
                clearInterval(interval);
                toast.dismiss(loadingToastId);
                toast.success("BOQ Updated Successfully");
                await getDynamicRequestData();
                handleClose();
            } else {
                clearInterval(interval);
                setUploadProgress(0);
                toast.error(`BOQ Update Error: ${res.statusText}`);
            }
        } catch (err) {
            clearInterval(interval);
            setUploadProgress(0);
            handleErrorToast(err, loadingToastId);
        } finally {
            clearInterval(interval);
            setTimeout(() => {
                setShowProgressModal(false);
            }, 1500);
            setLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    useEffect(() => {
        if (!dynamicStagesLevel) return; // guard: data abhi ready nahi

        if (dynamicStagesLevel?.role) {
            setFormData((prevFormData) => ({
                ...prevFormData,
                role: dynamicStagesLevel.role,
            }));
        } else {
            setFormData((prevFormData) => ({
                ...prevFormData,
                employee: dynamicStagesLevel.employee,
            }));
        }
    }, [dynamicStagesLevel]);

    return (
        <>
            <style>{`
                .drva-info-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 12px 20px;
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 14px 18px;
                    margin-bottom: 18px;
                }
                .drva-info-item {
                    display: flex;
                    flex-direction: column;
                    gap: 3px;
                }
                .drva-info-label {
                    font-size: 11px;
                    font-weight: 600;
                    color: #94a3b8;
                    text-transform: uppercase;
                    letter-spacing: 0.4px;
                }
                .drva-info-value {
                    font-size: 13.5px;
                    color: #1f2937;
                    font-weight: 600;
                }
            `}</style>

            <button className="model-delete-button-modern actionn-icon" style={{ backgroundColor: "#00000075" }} title={`Tender Stage ${level} Rejection`} onClick={handleShow}>
                <X size={20} color='white' />
            </button>

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Add" />
            ) : (
                <Modal show={show} onHide={handleClose} dialogClassName="modern-project-modal" size="lg">
                    <Modal.Header className="modern-modal-header">
                        <div className="header-content">
                            <div style={{ color: "white" }}>
                                <XCircle size={20} />
                            </div>
                            <div>
                                <Modal.Title>Reject Stage {level} Approval</Modal.Title>
                                <p className="header-subtitle" style={{ color: "white" }}>
                                    {approverLabel} • BOQ "{i?.project_short_name}"
                                </p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={handleClose}>
                            <X size={20} />
                        </button>
                    </Modal.Header>

                    <Modal.Body className="modern-modal-body">
                        <div className="drva-info-grid">
                            <div className="drva-info-item">
                                <span className="drva-info-label">Project Code</span>
                                <span className="drva-info-value">{i?.site_prcode}</span>
                            </div>
                            <div className="drva-info-item">
                                <span className="drva-info-label">Project Name</span>
                                <span className="drva-info-value">{i?.project_short_name}</span>
                            </div>
                            <div className="drva-info-item">
                                <span className="drva-info-label">Authority Engineer</span>
                                <span className="drva-info-value">{i?.authority_engineer_name || "N/A"}</span>
                            </div>
                            <div className="drva-info-item">
                                <span className="drva-info-label">Stage</span>
                                <span className="drva-info-value">Level {level} • {approverLabel}</span>
                            </div>
                        </div>

                        <div className="section-title">
                            <FileText size={18} />
                            <h4>Rejection Reason</h4>
                        </div>

                        <div className="flex-column">
                            <label htmlFor="remarks" className="form-labels font-weight500 announce-date font-weight400 font-size-heading">
                                Remarks
                            </label>
                            <textarea
                                id="remarks"
                                type="text"
                                name="remarks"
                                placeholder="Your Reason"
                                onChange={handleInputChange}
                                maxLength={100}
                                value={formData.remarks}
                                className={`form-input-textarea font-weight400 font-size-subheading ${errors.remarks
                                    ? "error"
                                    : inputState.remarks
                                        ? "success"
                                        : ""
                                    }`}
                            />
                            {errors.remarks && (
                                <span className="error-message font-size-text">{errors.remarks}</span>
                            )}
                        </div>

                        <div className="form-actions">
                            <button type="button" className="btn-secondaryy" onClick={handleClose}>
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn-primaryy"
                                onClick={dynamicApprovalLevelfilter ? handleFormSubmitPut : handleFormSubmitPost}
                                disabled={loading}
                            >
                                {loading ? <Loader size={18} className="spinning" /> : <X size={18} />}
                                <span>{loading ? 'Rejecting...' : 'Reject'}</span>
                            </button>
                        </div>

                        <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};


export {
    DynamicRequestLogTimeline,

    DynamicRequestImprestApproval,
    DynamicRequestImprestEditApproval,
    DynamicRequestImprestRejection,
    DynamicRequestImprestFinalize,

    DynamicRequestExpenseApproval,
    DynamicRequestExpensePartialEdit,
    DynamicRequestExpenseFinalize,
    DynamicRequestExpenseRejection,

    DynamicRequestOfficeApproval,

    DynamicRequestOfficeRejection,

    DynamicRequestVehicleApproval,
    DynamicRequestVehicleRejection,
    DynamicBillingFinal,
    DynamicRequestOfficeEditApproval,
    DynamicRequestVehicleEditApproval,
    DynamicVehicleFinal,

    DynamicRequestTenderApproval,
    DynamicRequestTenderEditApproval,
    DynamicRequestTenderRejection,
    DynamicRequestTenderFinalize,
}
